<template>
    <md-dialog md-open-from="#upgradeAcct" md-close-to="#upgradeAcct" ref="upgradeAcct" @open="fixContent" @close="startOver" class="upgrade-account-modal" :class="{'has-plan': user.active_pay, 'md-theme-show-mode': showMode, 'md-theme-default': !showMode, 'show-md-dark-mode': showMode}">
        <md-dialog-content class="upgrade-account-modal-content" :class="{'show-md-dark-mode': showMode}">
            <div class="close_modal" @click="cancel">&times;</div>
            <div class="modal-title" v-if="!user.active_pay">
                <span v-if="isIndividual">Choose your subscription plan</span>
                <span v-else-if="isInstitutional">Educational and Institutional Plan</span>
                <span v-else-if="isStudentMode">Cuelist Student</span>
                <span v-else>Cuelist Show Pass</span>
            </div>
            <div class="modal-title" v-else>
                <span v-if="isIndividual || isStudentMode">Switch your subscription plan</span>
                <span v-else>Educational and Institutional Plan</span>
            </div>
            <div
                class="plan-state-banner plan-state-banner--warning"
                v-if="partnerGrantConflictMessage"
                style="margin-bottom: 16px;">
                {{ partnerGrantConflictMessage }}
            </div>
            <div v-if="!user.active_pay">
                <div class="plan-type">
                    <span v-if="isIndividual" @click="setModalMode('institutional')">See our educational and institutional subscription options</span>
                    <span v-if="isInstitutional" @click="setModalMode('individual')">See our individual user subscription options</span>
                    <span v-if="isIndividual && studentPlansConfigured" @click="setModalMode('student')">Are you a student? See Cuelist student pricing</span>
                    <span v-if="isStudentMode" @click="setModalMode('individual')">See our individual subscription plans</span>
                    <!-- A different product, so set apart from the plan links (Josh, 15 Oct 2026). -->
                    <span v-if="!isShowPass && !isStudentMode && showPassPriceId" class="plan-type-apart" @click="setModalMode('showpass')">Just one production? See the Cuelist Show Pass</span>
                    <span v-if="isShowPass" @click="setModalMode('individual')">See our subscription plans</span>
                </div>
                <div class="plan-options-wrapper" v-if="isIndividual">
                    <div class="plan-cards plan-cards--individual">
                        <div
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'monthly',
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow">Cuelist</div>
                            <div class="plan-card-title">Cuelist Monthly</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$12.99</span>
                                <span class="plan-card-interval">/month</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn plan-card-btn--primary"
                                @click="setActivePlan('monthly')">
                                Choose plan
                            </button>
                        </div>
                        <div
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'yearly',
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow">Cuelist</div>
                            <div class="plan-card-title">Cuelist Yearly</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$129</span>
                                <span class="plan-card-interval">/year</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn plan-card-btn--primary"
                                @click="setActivePlan('yearly')">
                                Choose plan
                            </button>
                        </div>
                        <div
                            v-if="dualMonthlyPlanId"
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'dual_monthly' && !dualPlansLocked,
                                'is-unavailable': dualPlansLocked,
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                            <div class="plan-card-title">Cuelist + Notes List Monthly</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$19.98</span>
                                <span class="plan-card-interval">/month</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn"
                                :class="dualPlansLocked ? 'plan-card-btn--disabled' : 'plan-card-btn--primary'"
                                :disabled="dualPlansLocked"
                                @click="!dualPlansLocked && setActivePlan('dual_monthly')">
                                <span v-if="showDualEligibilityLoader">Checking…</span>
                                <span v-else-if="dualPlansLocked">Unavailable</span>
                                <span v-else>Choose plan</span>
                            </button>
                        </div>
                        <div
                            v-if="dualYearlyPlanId"
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'dual_yearly' && !dualPlansLocked,
                                'is-unavailable': dualPlansLocked,
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                            <div class="plan-card-title">Cuelist + Notes List Yearly</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$198</span>
                                <span class="plan-card-interval">/year</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn"
                                :class="dualPlansLocked ? 'plan-card-btn--disabled' : 'plan-card-btn--primary'"
                                :disabled="dualPlansLocked"
                                @click="!dualPlansLocked && setActivePlan('dual_yearly')">
                                <span v-if="showDualEligibilityLoader">Checking…</span>
                                <span v-else-if="dualPlansLocked">Unavailable</span>
                                <span v-else>Choose plan</span>
                            </button>
                        </div>
                    </div>
                    <div class="plan-state-banner plan-state-banner--info" v-if="!eligibilityLoading && planModalState === 1">
                        Connect your Notes List account to check eligibility for Cuelist + Notes List plans.
                        <a href="#" @click.prevent="goConnectNotesList">Connect account →</a>
                    </div>
                    <div class="plan-state-banner plan-state-banner--success" v-else-if="!eligibilityLoading && planModalState === 2">
                        Your Notes List account already has an active subscription — you only need Cuelist. Choose Cuelist Monthly or Yearly above.
                        <template v-if="dualEligibleAtDate">Dual becomes available once that subscription ends on <strong>{{ dualEligibleAtDate }}</strong>.</template>
                        If you are interested in a discounted dual subscription, please complete your purchase at thenoteslist.com
                    </div>
                </div>
                <div class="plan-options-wrapper" v-else-if="isStudentMode">
                    <div class="plan-state-banner plan-state-banner--warning" v-if="studentState.state === 'ended'">
                        {{ studentEndedText }}
                    </div>
                    <div class="plan-state-banner plan-state-banner--info" v-else-if="studentState.state === 'returning'">
                        {{ studentReturningText }}
                    </div>
                    <div class="plan-cards plan-cards--student">
                        <div
                            v-for="card in studentCards"
                            :key="card.plan"
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === card.plan && !studentUnavailable,
                                'is-unavailable': studentUnavailable,
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow">Cuelist Student</div>
                            <div class="plan-card-title">{{ card.title }}</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">{{ card.amount }}</span>
                                <span class="plan-card-interval">{{ card.per }}</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn"
                                :class="studentUnavailable ? 'plan-card-btn--disabled' : 'plan-card-btn--primary'"
                                :disabled="studentUnavailable"
                                @click="!studentUnavailable && setActivePlan(card.plan)">
                                <span v-if="studentUnavailable">Unavailable</span>
                                <span v-else>Choose plan</span>
                            </button>
                        </div>
                    </div>
                    <div class="plan-options-note">
                        <p>Student pricing is good for four years from the date you first subscribe. After that, your plan renews at the regular Cuelist price.</p>
                        <p>Promotion codes do not apply.</p>
                    </div>
                </div>
                <div class="plan-options-wrapper" v-else-if="isShowPass">
                    <div class="plan-cards plan-cards--showpass">
                        <div class="plan-card is-selected" :class="{'show-md-dark-mode': showMode}">
                            <div class="plan-card-eyebrow">Cuelist</div>
                            <div class="plan-card-title">Cuelist Show Pass</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$299</span>
                                <span class="plan-card-interval">one-time, one production</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="plan-options-wrapper" v-else>
                    <div class="plan-cards plan-cards--edu-inst">
                        <div
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'educational',
                                'is-unavailable': !hasEduEmail,
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow">Cuelist</div>
                            <div class="plan-card-title">Educational Plan</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$249</span>
                                <span class="plan-card-interval">/year</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn"
                                :class="hasEduEmail ? 'plan-card-btn--primary' : 'plan-card-btn--disabled'"
                                :disabled="!hasEduEmail"
                                @click="hasEduEmail && setActivePlan('educational')">
                                <span v-if="!hasEduEmail">.edu required</span>
                                <span v-else>Choose plan</span>
                            </button>
                        </div>
                        <div
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'institutional',
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow">Cuelist</div>
                            <div class="plan-card-title">Institutional Plan</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$349</span>
                                <span class="plan-card-interval">/year</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn plan-card-btn--primary"
                                @click="setActivePlan('institutional')">
                                Choose plan
                            </button>
                        </div>
                        <div
                            v-if="dualEducationalPlanId"
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'dual_educational' && !dualEduUnavailable,
                                'is-unavailable': dualEduUnavailable,
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                            <div class="plan-card-title">Cuelist + Notes List Educational</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$398</span>
                                <span class="plan-card-interval">/year</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn"
                                :class="dualEduUnavailable ? 'plan-card-btn--disabled' : 'plan-card-btn--primary'"
                                :disabled="dualEduUnavailable"
                                @click="!dualEduUnavailable && setActivePlan('dual_educational')">
                                <span v-if="showDualEligibilityLoader">Checking…</span>
                                <span v-else-if="bundleDisabled">Unavailable</span>
                                <span v-else-if="!hasEduEmail">.edu required</span>
                                <span v-else-if="!notesListLinked">Unavailable</span>
                                <span v-else>Choose plan</span>
                            </button>
                        </div>
                        <div
                            v-if="resolvedDualInstitutionPlanId"
                            class="plan-card"
                            :class="{
                                'is-selected': activePlan === 'dual_institutional' && !dualInstUnavailable,
                                'is-unavailable': dualInstUnavailable,
                                'show-md-dark-mode': showMode
                            }">
                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                            <div class="plan-card-title">Cuelist + Notes List Institutional</div>
                            <div class="plan-card-price">
                                <span class="plan-card-amount">$598</span>
                                <span class="plan-card-interval">/year</span>
                            </div>
                            <button
                                type="button"
                                class="plan-card-btn"
                                :class="dualInstUnavailable ? 'plan-card-btn--disabled' : 'plan-card-btn--primary'"
                                :disabled="dualInstUnavailable"
                                @click="!dualInstUnavailable && setActivePlan('dual_institutional')">
                                <span v-if="showDualEligibilityLoader">Checking…</span>
                                <span v-else-if="bundleDisabled">Unavailable</span>
                                <span v-else-if="!notesListLinked">Unavailable</span>
                                <span v-else>Choose plan</span>
                            </button>
                        </div>
                    </div>
                    <div class="plan-state-banner plan-state-banner--info" v-if="!eligibilityLoading && planModalState === 1">
                        Connect your Notes List account to check eligibility for Cuelist + Notes List Dual plans.
                        <a href="#" @click.prevent="goConnectNotesList">Connect account →</a>
                    </div>
                    <div class="plan-state-banner plan-state-banner--success" v-else-if="!eligibilityLoading && planModalState === 2">
                        Your Notes List account already has an active subscription — Dual Educational / Institutional are unavailable. Choose Cuelist Educational or Institutional above.
                        <template v-if="dualEligibleAtDate"> Dual becomes available once that subscription ends on <strong>{{ dualEligibleAtDate }}</strong>.</template>
                    </div>
                    <div class="plan-options-note">
                        <p>Educational plans require a .edu email address or equivalent.</p>
                        <p>Additional seats can be added from Account Settings after purchase.</p>
                        <p>Dual team seats must also be invited in The Notes List.</p>
                    </div>
                </div>
            </div>
            <div class="plan-subscribe-content-wrapper">
                <md-layout class="plan-subscribe-content-container">
                    <md-layout :md-flex="user.active_pay ? 100 : 60">
                        <div class="plan-subscribe-content">
                            <div v-if="!user.active_pay">
                                <!-- Subscriptions only. A Show Pass is mode: "payment"
                                     (functions/index.js) and never gets
                                     trial_period_days, so promising a trial here
                                     contradicted the one-time-purchase notice directly
                                     below it and promised something checkout does not
                                     honour. -->
                                <p v-if="!isShowPass">Free 15 day trial on all first-time subscriptions</p>
                                <p>Your payments are SSL encrypted, which means they are 100% safe with us.</p>
                                <div class="plan-payment-notification" :class="{'show-md-dark-mode': showMode}">
                                    <div class="notification-title">
                                        <span v-if="isShowPass">Cuelist Show Pass</span>
                                        <span v-else>Cuelist {{strings.plan_label}} Plan Payment</span>
                                    </div>
                                    <div class="notification-message" v-if="isShowPass">
                                        You will be charged <span>${{ strings.price }} one time</span> for a single production.<br/>
                                        This is a one-time purchase, not a subscription &mdash; it permanently unlocks one production for unlimited collaborators.<br/>
                                        There are no recurring charges.
                                    </div>
                                    <div class="notification-message" v-else>
                                        You will be charged <span>${{ (promoApplied && promoData && promoData.valid) ? (strings.price - (promoData.amount_off ? (promoData.amount_off / 100) : (strings.price * promoData.percent_off / 100))).toFixed(2) : strings.price }} per {{strings.cycle}} starting {{strings.tbd}}</span>.<br/>
                                        Your subscription will automatically renew {{strings.cycle}}ly on a recurring basis.<br/>
                                        Cancel your auto renew at any time on your Account Settings page.
                                    </div>
                                    <div class="notification-message plan-student-end" v-if="isStudentPlanSelected && !studentUnavailable">
                                        {{ studentEndLineForSelected }}
                                    </div>
                                    <div class="notification-message" v-if="isInstitutional">
                                        {{strings.plan_label}} plan includes <span>five active individual accounts</span> at any one time.<br/>
                                        Plan administrator can change active users at any point during the subscription period.<br/>
                                        Additional user licenses can be purchased at a discounted rate after checkout at the MANAGE ACTIVE USERS portal.
                                    </div>
                                    <div class="notification-message" v-if="activePlan == 'educational'">
                                        Educational pricing requires a .edu email address or equivalent on your account.
                                    </div>
                                    <div class="notification-message" v-if="activePlan == 'educational' && !hasEduEmail" style="color: #c62828;">
                                        Your account email is not a .edu address. Update your email or choose Institutional / Individual.
                                    </div>
                                </div>
                                <div class="subscription-terms-privacy">
                                    <md-checkbox v-model="termsandpolicy" v-if="isDualPlanSelected">
                                        By proceeding you agree to the Cuelist <a href="https://thecuelist.com/terms-of-use" target="_blank" rel="noopener noreferrer" @click.stop>Terms of Use</a> and <a href="https://thecuelist.com/privacy" target="_blank" rel="noopener noreferrer" @click.stop>Privacy Policy</a>, and The Notes List <a href="https://thenoteslist.com/policies/terms" target="_blank" rel="noopener noreferrer" @click.stop>Terms of Service</a> and <a href="https://thenoteslist.com/policies/privacy" target="_blank" rel="noopener noreferrer" @click.stop>Privacy Policy</a>.
                                    </md-checkbox>
                                    <md-checkbox v-model="termsandpolicy" v-else>
                                        By proceeding you agree to the <a href="https://thecuelist.com/terms-of-use" target="_blank" rel="noopener noreferrer" @click.stop>Terms of Use</a> and <a href="https://thecuelist.com/privacy" target="_blank" rel="noopener noreferrer" @click.stop>Privacy Policy</a>.
                                    </md-checkbox>
                                </div>
                                <div class="plan-subscribe-button-wrapper">
                                    <form method="POST" :action="actionUrl" ref="checkoutForm" @submit.prevent="submitCheckout">
                                        <md-button class="md-raised btn-subscribe-plan" type="submit" :disabled="!canSubmitCheckout">Checkout</md-button>
                                        <input v-if="isShowPass" type='hidden' name="mode" value="payment" />
                                        <!-- A refused student checkout comes back to the app instead of raw JSON (functions checkoutRefusal). -->
                                        <input v-if="isStudentPlanSelected" type='hidden' name="on_error" value="redirect" />
                                        <input type='hidden' name="customer_id" :value="user.stripe_id" />
                                        <input type='hidden' name="product" :value="activePlanId" />
                                        <input type='hidden' name="email_id" :value="user.email" />
                                        <input type='hidden' name="user_id" :value="user.uid" />
                                    </form>
                                </div>
                            </div>
                            <div v-else class="plan-switch-panel">
                                <p class="plan-current-line">
                                    Current plan:
                                    <span class="plan-current-name">{{ isOnDual ? currentDualLabel : currentPlanLabel }}</span>
                                    <span v-if="isOnStudentPrice" class="plan-current-student-end">{{ studentEndLineForCurrent }}</span>
                                </p>
                                <div
                                    v-if="!showCancelConfirm"
                                    class="plan-type">
                                    <span v-if="isIndividual" @click="setModalMode('institutional')">See our educational and institutional subscription options</span>
                                    <span v-if="isIndividual && canSeeStudentFromSwitch" @click="setModalMode('student')">Are you a student? See Cuelist student pricing</span>
                                    <span v-if="isStudentMode && isOnStudentPrice" @click="setModalMode('individual')">See regular Cuelist pricing</span>
                                    <span v-if="isStudentMode && isOnStudentPrice" @click="setModalMode('institutional')">See our educational and institutional subscription options</span>
                                    <span v-if="isStudentMode && !isOnStudentPrice" @click="setModalMode('individual')">See our individual subscription plans</span>
                                    <span v-if="isInstitutional" @click="setModalMode('individual')">See our individual user subscription options</span>
                                </div>

                                <!-- Switch / upgrade cards (hidden while confirming cancel) -->
                                <template v-if="!showCancelConfirm">
                                <div
                                    v-if="isIndividual"
                                    class="plan-cards plan-cards--individual">
                                    <!-- Cuelist Monthly -->
                                    <div
                                        class="plan-card"
                                        :class="{
                                            'is-current': isCurrentMonthly && !isOnDual && !isOnStudentPrice,
                                            'is-unavailable': isOnEduInstFamily || isOnStudentPrice || (user.pending_cancel && !(isCurrentMonthly && !isOnDual)),
                                            'is-pending-confirm': isPendingCuelistMonthly,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow">Cuelist</div>
                                            <span v-if="isCurrentMonthly && !isOnDual && !isOnStudentPrice" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Cuelist Monthly</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$12.99</span>
                                            <span class="plan-card-interval">/month</span>
                                        </div>
                                        <button
                                            v-if="isCurrentMonthly && !isOnDual && !isOnStudentPrice"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="isOnDualIndividual && !user.pending_cancel"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectDowngradeTarget('month')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('downgrade', 'month')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('downgrade', 'month') ? 'Switching…' : 'Switch plan' }}
                                        </button>
                                        <button
                                            v-else-if="isCurrentYearly && !isOnDual && !isOnStudentPrice && !user.pending_cancel"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectCuelistIntervalSwitch('month')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('cuelist_interval', 'month')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('cuelist_interval', 'month') ? 'Switching…' : 'Switch plan' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                    <!-- Cuelist Yearly -->
                                    <div
                                        class="plan-card"
                                        :class="{
                                            'is-current': isCurrentYearly && !isOnDual && !isOnStudentPrice,
                                            'is-unavailable': isOnEduInstFamily || isOnStudentPrice || (user.pending_cancel && !(isCurrentYearly && !isOnDual)),
                                            'is-pending-confirm': isPendingCuelistYearly,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow">Cuelist</div>
                                            <span v-if="isCurrentYearly && !isOnDual && !isOnStudentPrice" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Cuelist Yearly</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$129</span>
                                            <span class="plan-card-interval">/year</span>
                                        </div>
                                        <button
                                            v-if="isCurrentYearly && !isOnDual && !isOnStudentPrice"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="isOnDualIndividual && !user.pending_cancel"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectDowngradeTarget('year')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('downgrade', 'year')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('downgrade', 'year') ? 'Switching…' : 'Switch plan' }}
                                        </button>
                                        <button
                                            v-else-if="isIndividualActivePlan && !isOnDual && !isOnStudentPrice && !isCurrentYearly && !user.pending_cancel"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectCuelistIntervalSwitch('year')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('cuelist_interval', 'year')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('cuelist_interval', 'year') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                    <!-- Dual Monthly -->
                                    <div
                                        v-if="dualMonthlyPlanId"
                                        class="plan-card"
                                        :class="{
                                            'is-current': isOnDualIndividual && currentDualInterval === 'month',
                                            'is-unavailable': (!isOnDualIndividual && !canUpgradeToDualMonthly) || isOnEduInstFamily,
                                            'is-pending-confirm': isPendingDualMonthly,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                                            <span v-if="isOnDualIndividual && currentDualInterval === 'month'" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Cuelist + Notes List Monthly</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$19.98</span>
                                            <span class="plan-card-interval">/month</span>
                                        </div>
                                        <button
                                            v-if="isOnDualIndividual && currentDualInterval === 'month'"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="isOnDualIndividual && currentDualInterval === 'year' && notesListLinked && !user.pending_cancel"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="switchWorking || dualUpgradeWorking || user.pending_cancel"
                                            @click="selectDualUpgrade('month')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('upgrade_dual', 'month')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('upgrade_dual', 'month') ? 'Switching…' : 'Switch plan' }}
                                        </button>
                                        <button
                                            v-else-if="canUpgradeToDualMonthly"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || dualUpgradeWorking || user.pending_cancel"
                                            @click="selectDualUpgrade('month')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('upgrade_dual', 'month')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('upgrade_dual', 'month') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                    <!-- Dual Yearly -->
                                    <div
                                        v-if="dualYearlyPlanId"
                                        class="plan-card"
                                        :class="{
                                            'is-current': isOnDualIndividual && currentDualInterval === 'year',
                                            'is-unavailable': (!isOnDualIndividual && !canUpgradeToDualYearly) || isOnEduInstFamily,
                                            'is-pending-confirm': isPendingDualYearly,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                                            <span v-if="isOnDualIndividual && currentDualInterval === 'year'" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Cuelist + Notes List Yearly</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$198</span>
                                            <span class="plan-card-interval">/year</span>
                                        </div>
                                        <button
                                            v-if="isOnDualIndividual && currentDualInterval === 'year'"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="isOnDualIndividual && currentDualInterval === 'month' && notesListLinked && !user.pending_cancel"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || dualUpgradeWorking || user.pending_cancel"
                                            @click="selectDualUpgrade('year')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('upgrade_dual', 'year')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('upgrade_dual', 'year') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else-if="canUpgradeToDualYearly"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || dualUpgradeWorking || user.pending_cancel"
                                            @click="selectDualUpgrade('year')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('upgrade_dual', 'year')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('upgrade_dual', 'year') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                </div>

                                <!-- Paid: student cards (switch down, or a student's own interval switch) -->
                                <template v-if="isStudentMode">
                                <div class="plan-state-banner plan-state-banner--warning" v-if="studentState.state === 'ended'">
                                    {{ studentEndedText }}
                                </div>
                                <div class="plan-state-banner plan-state-banner--info" v-else-if="studentState.state === 'returning'">
                                    {{ studentReturningText }}
                                </div>
                                <div class="plan-cards plan-cards--student">
                                    <div
                                        v-for="card in studentCards"
                                        :key="card.plan"
                                        class="plan-card"
                                        :class="{
                                            'is-current': studentCurrentInterval === card.interval,
                                            'is-unavailable': studentCurrentInterval !== card.interval && !studentCardAction(card),
                                            'is-pending-confirm': isPendingStudent(card.interval),
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow">Cuelist Student</div>
                                            <span v-if="studentCurrentInterval === card.interval" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">{{ card.title }}</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">{{ card.amount }}</span>
                                            <span class="plan-card-interval">{{ card.per }}</span>
                                        </div>
                                        <button
                                            v-if="studentCurrentInterval === card.interval"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="studentCardAction(card) === 'upgrade'"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectCuelistIntervalSwitch(card.interval)">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('cuelist_interval', card.interval)" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('cuelist_interval', card.interval) ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else-if="studentCardAction(card) === 'interval'"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectCuelistIntervalSwitch(card.interval)">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('cuelist_interval', card.interval)" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('cuelist_interval', card.interval) ? 'Switching…' : 'Switch plan' }}
                                        </button>
                                        <button
                                            v-else-if="studentCardAction(card) === 'switch'"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectStudentSwitch(card.interval)">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('student', card.interval)" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('student', card.interval) ? 'Switching…' : 'Switch plan' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                </div>
                                </template>

                                <!-- Paid: Educational / Institutional cards -->
                                <div
                                    v-if="isInstitutional"
                                    class="plan-cards plan-cards--edu-inst">
                                    <div
                                        class="plan-card"
                                        :class="{
                                            'is-current': isCurrentEducational,
                                            'is-unavailable': !isCurrentEducational && !canUpgradeToEducational,
                                            'is-pending-confirm': isPendingEducational,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow">Cuelist</div>
                                            <span v-if="isCurrentEducational" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Educational Plan</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$249</span>
                                            <span class="plan-card-interval">/year</span>
                                        </div>
                                        <button
                                            v-if="isCurrentEducational"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="canUpgradeToEducational"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectTeamUpgrade('educational')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('team_upgrade', 'educational')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('team_upgrade', 'educational') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else-if="!hasEduEmail && canUpgradeToCuelistTeamBase"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            .edu required
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                    <div
                                        class="plan-card"
                                        :class="{
                                            'is-current': isCurrentInstitutional,
                                            'is-unavailable': !isCurrentInstitutional && !canUpgradeToInstitutional,
                                            'is-pending-confirm': isPendingInstitutional,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow">Cuelist</div>
                                            <span v-if="isCurrentInstitutional" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Institutional Plan</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$349</span>
                                            <span class="plan-card-interval">/year</span>
                                        </div>
                                        <button
                                            v-if="isCurrentInstitutional"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="canUpgradeToInstitutional"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectTeamUpgrade('institutional')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('team_upgrade', 'institutional')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('team_upgrade', 'institutional') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                    <div
                                        v-if="dualEducationalPlanId"
                                        class="plan-card"
                                        :class="{
                                            'is-current': isCurrentDualEducational,
                                            'is-unavailable': !isCurrentDualEducational && !canUpgradeToDualEducational,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                                            <span v-if="isCurrentDualEducational" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Cuelist + Notes List Educational</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$398</span>
                                            <span class="plan-card-interval">/year</span>
                                        </div>
                                        <button
                                            v-if="isCurrentDualEducational"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="canUpgradeToDualEducational"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectTeamUpgrade('dual_educational')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('team_upgrade', 'dual_educational')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('team_upgrade', 'dual_educational') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                    <div
                                        v-if="resolvedDualInstitutionPlanId"
                                        class="plan-card"
                                        :class="{
                                            'is-current': isCurrentDualInstitutional,
                                            'is-unavailable': !isCurrentDualInstitutional && !canUpgradeToDualInstitutional,
                                            'show-md-dark-mode': showMode
                                        }">
                                        <div class="plan-card-top">
                                            <div class="plan-card-eyebrow plan-card-eyebrow--dual">Cuelist + Notes List</div>
                                            <span v-if="isCurrentDualInstitutional" class="plan-card-badge">Current</span>
                                        </div>
                                        <div class="plan-card-title">Cuelist + Notes List Institutional</div>
                                        <div class="plan-card-price">
                                            <span class="plan-card-amount">$598</span>
                                            <span class="plan-card-interval">/year</span>
                                        </div>
                                        <button
                                            v-if="isCurrentDualInstitutional"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--outline"
                                            :disabled="!user.stripe_id"
                                            @click="openManageBilling">
                                            Manage Billing
                                        </button>
                                        <button
                                            v-else-if="canUpgradeToDualInstitutional"
                                            type="button"
                                            class="plan-card-btn plan-card-btn--primary"
                                            :disabled="switchWorking || user.pending_cancel"
                                            @click="selectTeamUpgrade('dual_institution')">
                                            <span class="plan-card-btn-spinner" v-if="isPlanActionBusy('team_upgrade', 'dual_institution')" aria-hidden="true"></span>
                                            {{ isPlanActionBusy('team_upgrade', 'dual_institution') ? 'Upgrading…' : 'Upgrade' }}
                                        </button>
                                        <button
                                            v-else
                                            type="button"
                                            class="plan-card-btn plan-card-btn--disabled"
                                            disabled>
                                            Unavailable
                                        </button>
                                    </div>
                                </div>
                                <div
                                    class="plan-state-banner plan-state-banner--info"
                                    v-if="isInstitutional && !eligibilityLoading && !notesListLinked"
                                    style="margin-top: 16px;">
                                    Connect your Notes List account to check eligibility for Cuelist + Notes List Dual plans.
                                    <a href="#" @click.prevent="goConnectNotesList">Connect account →</a>
                                </div>
                                <div
                                    class="plan-state-banner plan-state-banner--success"
                                    v-else-if="isInstitutional && !eligibilityLoading && showDualAlreadyLicensedBanner"
                                    style="margin-top: 16px;">
                                    Your Notes List account already has an active subscription — Dual Educational / Institutional are unavailable. Choose Cuelist Educational or Institutional above.
                                    <template v-if="dualEligibleAtDate"> Dual becomes available once that subscription ends on <strong>{{ dualEligibleAtDate }}</strong>.</template>
                                </div>

                                <div v-if="user.pending_downgrade" class="plan-state-banner plan-state-banner--info plan-state-banner--with-action" style="margin-top: 16px;">
                                    <span>
                                        A switch to Cuelist Individual is scheduled for {{ formatDowngradeAt(user.downgrade_at) }}.
                                        You keep Dual access until then.
                                    </span>
                                    <button type="button" class="plan-banner-action-btn" :disabled="switchWorking" @click="cancelScheduledDowngrade">
                                        <span class="plan-banner-action-spinner" v-if="switchWorking && !pendingConfirmAction" aria-hidden="true"></span>
                                        {{ switchWorking && !pendingConfirmAction ? 'Keeping…' : 'Keep Dual' }}
                                    </button>
                                </div>
                                <div v-if="user.pending_cancel" class="plan-state-banner plan-state-banner--warning plan-state-banner--with-action" style="margin-top: 16px;">
                                    <span>
                                        Your subscription is scheduled to cancel{{ cancelPeriodEndDate ? ' on ' + cancelPeriodEndDate : ' at the end of the billing period' }}.
                                        You keep access until then.
                                        <br>
                                        Plan switching is disabled while a cancellation is scheduled — keep your plan first if you want to switch.
                                    </span>
                                    <button type="button" class="plan-banner-action-btn" :disabled="doing" @click="keepScheduledCuelistCancellation">
                                        <span class="plan-banner-action-spinner" v-if="doing" aria-hidden="true"></span>
                                        {{ doing ? 'Keeping…' : 'Keep my plan' }}
                                    </button>
                                </div>
                                <div v-if="pendingConfirmAction" class="plan-confirm-panel">
                                    <div class="plan-confirm-panel-header">
                                        <svg class="plan-confirm-panel-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                                            <rect x="4.5" y="10.5" width="15" height="9" rx="2" stroke="currentColor" stroke-width="1.8"/>
                                            <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                        </svg>
                                        <span class="plan-confirm-panel-title">
                                            {{ pendingConfirmAction.type === 'downgrade' || pendingConfirmAction.type === 'student' ? 'Confirm switch' : 'Confirm payment' }}
                                        </span>
                                    </div>
                                    <p v-if="pendingConfirmSummary" class="plan-confirm-panel-plan">{{ pendingConfirmSummary }}</p>
                                    <p v-if="switchWorking && !switchPreview && !switchError" class="plan-confirm-panel-amount">
                                        Checking amount…
                                    </p>
                                    <p v-else-if="switchPreview" class="plan-confirm-panel-amount">
                                        <span v-if="switchPreview.deferred">{{ switchPreview.message }}</span>
                                        <span v-else-if="switchPreview.same_price">You're already on this plan.</span>
                                        <span v-else-if="isTrialing && trialEndDate">
                                            You're still in your free trial — switching now keeps your remaining trial days at no charge. Your new plan's pricing starts on <strong>{{ trialEndDate }}</strong>, when your trial ends.
                                        </span>
                                        <span v-else>Estimated charge today: <strong>${{ switchPreview.amount_due_display || '0.00' }}</strong></span>
                                    </p>
                                    <p v-if="switchPreview && confirmExtraLines.length" class="plan-confirm-panel-amount">
                                        <template v-for="(line, i) in confirmExtraLines"><br v-if="i" :key="'br' + i"/><span :key="'l' + i">{{ line }}</span></template>
                                    </p>
                                    <p v-if="switchError" class="plan-confirm-panel-error">{{ switchError }}</p>
                                    <div class="plan-confirm-panel-actions">
                                        <md-button
                                            class="md-raised btn-subscribe-plan"
                                            :disabled="switchWorking || dualUpgradeWorking || doing || user.pending_cancel || !(switchPreview || pendingConfirmAction.type === 'downgrade')"
                                            @click="runPendingConfirmAction">
                                            <span class="plan-card-btn-spinner" v-if="switchWorking || dualUpgradeWorking || doing" aria-hidden="true"></span>
                                            {{ pendingConfirmLabel }}
                                        </md-button>
                                        <a
                                            href="#"
                                            class="plan-confirm-panel-cancel"
                                            @click.prevent="clearPendingSwitch">
                                            Cancel
                                        </a>
                                    </div>
                                </div>

                                <div
                                    class="plan-state-banner plan-state-banner--info"
                                    v-if="isIndividual && !eligibilityLoading && !notesListLinked"
                                    style="margin-top: 16px;">
                                    Connect your Notes List account to check eligibility for Cuelist + Notes List plans.
                                    <a href="#" @click.prevent="goConnectNotesList">Connect account →</a>
                                </div>
                                <div
                                    class="plan-state-banner plan-state-banner--success"
                                    v-else-if="isIndividual && !eligibilityLoading && showDualAlreadyLicensedBanner"
                                    style="margin-top: 16px;">
                                    Your Notes List account has its own active subscription, so Cuelist + Notes List plans aren't available.
                                    Your current Cuelist plan already works alongside Notes List - there's nothing more to buy.
                                    <template v-if="dualEligibleAtDate">Dual becomes available once that subscription ends on <strong>{{ dualEligibleAtDate }}</strong>.</template>
                                    If you’d prefer a discounted dual subscription, you can purchase it at thenoteslist.com
                                </div>

                                <!-- Footer: Cancel subscription -->
                                <div class="plan-cancel-footer" v-if="!user.pending_cancel">
                                    <button
                                        type="button"
                                        class="plan-cancel-link"
                                        :disabled="!user.subscription_id"
                                        @click="openCancelConfirm">
                                        Cancel subscription
                                    </button>
                                </div>
                                </template>

                                <!-- Cancel confirmation only (design state 6–7) -->
                                <div class="plan-cancel-box" v-if="showCancelConfirm">
                                    <div class="plan-cancel-box-title">Cancel your subscription?</div>
                                    <p class="plan-cancel-box-body" v-if="isOnDual">
                                        <template v-if="cancelPeriodEndDate">
                                            This ends <strong>both your Cuelist and Notes List</strong> access on
                                            <strong>{{ cancelPeriodEndDate }}</strong> — the end of your current billing period, including structure sync.
                                        </template>
                                        <template v-else>
                                            This ends <strong>both your Cuelist and Notes List</strong> access at
                                            <strong>the end of your current billing period</strong>, including structure sync.
                                        </template>
                                    </p>
                                    <p class="plan-cancel-box-body" v-else>
                                        <template v-if="cancelPeriodEndDate">
                                            This ends your Cuelist access on
                                            <strong>{{ cancelPeriodEndDate }}</strong> — the end of your current billing period. You’ll keep full access until then.
                                        </template>
                                        <template v-else>
                                            This ends your Cuelist access at
                                            <strong>the end of your current billing period</strong>. You’ll keep full access until then.
                                        </template>
                                    </p>
                                    <div class="plan-cancel-note" v-if="!isOnDual">
                                        If you separately subscribe to Notes List, that subscription is unaffected — manage it from your Notes List account settings. Structure sync between the apps will stop once your Cuelist plan ends.
                                    </div>
                                    <div v-if="switchError" class="notification-message" style="color: #c62828; margin-top: 8px;">{{ switchError }}</div>
                                    <div class="plan-cancel-actions">
                                        <button type="button" class="plan-cancel-btn plan-cancel-btn--keep" :disabled="doing" @click="showCancelConfirm = false; switchError = ''">
                                            Keep my plan
                                        </button>
                                        <button type="button" class="plan-cancel-btn plan-cancel-btn--confirm" :disabled="doing" @click="confirmCancelSubscription">
                                            <span class="plan-cancel-btn-spinner" v-if="doing" aria-hidden="true"></span>
                                            {{ doing ? 'Canceling…' : 'Cancel subscription' }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </md-layout>
                    <md-layout md-flex="40" v-if="!user.active_pay">
                        <div class="billing-details" :class="{'show-md-dark-mode': showMode}">
                            <div class="billing-details-title">
                                <span v-if="isShowPass">Cuelist Show Pass</span>
                                <span v-else-if="isStudentPlanSelected">Cuelist {{strings.plan_label}} Account</span>
                                <span v-else>Cuelist {{strings.plan_label}} Collaborator Account</span>
                            </div>
                            <ul class="billing-details-checklist">
                                <li v-for="text in strings.checklist">{{text}}</li>
                            </ul>
                            <div class="billing-details-list billing-details-list--showpass" v-if="isShowPass">
                                <div class="showpass-row">
                                    <span>Show Pass:</span>
                                    <span>${{strings.price}}</span>
                                </div>
                                <div class="showpass-row showpass-row--total">
                                    <span>Total (one-time):</span>
                                    <span>${{strings.price}}</span>
                                </div>
                            </div>
                            <div class="billing-details-list" v-else>
                                <md-layout>
                                    <md-layout md-flex="65">
                                        {{strings.cycle_label}} Subscription:
                                    </md-layout>
                                    <md-layout md-flex="35">
                                        ${{strings.price}}
                                    </md-layout>
                                </md-layout>
                                <md-layout v-if="promoApplied && promoData && promoData.valid">
                                    <md-layout md-flex="65">
                                        Coupon Discount:
                                    </md-layout>
                                    <md-layout md-flex="35">
                                        ${{ promoData.amount_off ? (promoData.amount_off / 100) : (strings.price * promoData.percent_off / 100).toFixed(2)}}
                                    </md-layout>
                                </md-layout>
                                <md-layout>
                                    <md-layout md-flex="65">
                                        Total billed on<br/>
                                        {{strings.tbd}}:
                                    </md-layout>
                                    <md-layout md-flex="35" md-vertical-align="end">
                                        ${{ (promoApplied && promoData && promoData.valid) ? (strings.price - (promoData.amount_off ? (promoData.amount_off / 100) : (strings.price * promoData.percent_off / 100))).toFixed(2) : strings.price }}
                                    </md-layout>
                                </md-layout>
                            </div>
                        </div>
                    </md-layout>
                </md-layout>
            </div>
        </md-dialog-content>
    </md-dialog>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { mapGetters } from 'vuex';
import { STRIPE_PUB_KEY } from '@/lib/global';
import { CLOUD_FUNCTIONS_BASE, CANCEL_PLAN_URL, CANCEL_PLAN_UNDO_URL, UPDATE_CARD_URL, CHECK_PROMOCODE_URL, UPDATE_SUBSCRIPTION_URL } from '@/lib/URLs';
import { fetchBundleEligibility } from '@/lib/noteslist-integration-access';
import { stashPendingPlanUpdate } from '@/lib/pending-plan-update';
import ensureStripeJsLoaded from '@/lib/load-stripe-js';
import { studentWindow, endLine, endedBanner, returningBanner, creditLines } from '@/lib/student-plan';

const UpgradeAccount = {
    name: 'upgradeAccount',
    data: function() {
        return {
            termsandpolicy: false,
            completed: false,
            modalMode: 'individual',
            // Set by open({ mode }) for the one open that follows (fixContent otherwise picks the user's own family).
            openMode: null,
            activePlan: 'monthly',
            activePlanId: `${process.env.VUE_APP_STRIPE_MONTHLTPLANID}`,
            dualMonthlyPlanId: process.env.VUE_APP_STRIPE_DUAL_MONTHLY_PLANID || '',
            dualYearlyPlanId: process.env.VUE_APP_STRIPE_DUAL_YEARLY_PLANID || '',
            dualEducationalPlanId: process.env.VUE_APP_STRIPE_DUAL_EDUCATIONAL_PLANID || '',
            dualInstitutionPlanId: process.env.VUE_APP_STRIPE_DUAL_INSTITUTION_PLANID || '',
            switchPreview: null,
            switchWorking: false,
            switchError: '',
            pendingConfirmAction: null,
            showCancelConfirm: false,
            cancelPeriodEndFetched: null,
            promocode: '',
            promoApplied: false,
            promoError: false,
            promoTimer: null,
            promoData: null,
            plans: {
                monthly: {
                    price: 12.99,
                    cycle: 'month',
                    cycle_label: 'Monthly',
                    plan_label: 'Monthly',
                    checklist: [
                        'Access to a secure, editable, digital archive of every project',
                        'Add cues and notes anywhere on the script',
                        'Save and restore old versions of every project',
                        'Intuitive and streamlined real-time functionality',
                        'Indespensible for stage managers and all creative team members'
                    ]
                },
                yearly: {
                    price: 129,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Annual',
                    checklist: [
                        'Access to a secure, editable, digital archive of every project',
                        'Add cues and notes anywhere on the script',
                        'Save and restore old versions of every project',
                        'Intuitive and streamlined real-time functionality',
                        'Indespensible for stage managers and all creative team members'
                    ]
                },
                educational: {
                    price: 249,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Educational',
                    checklist: [
                        'Access to a secure, editable, digital archive of every project',
                        'Add cues and notes anywhere on the script',
                        'Add, replace or delete script pages with ease',
                        'Save and restore old versions of every project',
                        'Intuitive and streamlined real-time functionality',
                        '5 active individual accounts at any one time',
                        'Swap in different active users at any point',
                        'A great way to remotely keep track of student work',
                        'Requires a .edu email address or equivalent'
                    ]
                },
                institutional: {
                    price: 349,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Institutional',
                    checklist: [
                        'Access to a secure, editable, digital archive of every project',
                        'Add cues and notes anywhere on the script',
                        'Add, replace or delete script pages with ease',
                        'Save and restore old versions of every project',
                        'Intuitive and streamlined real-time functionality',
                        '5 active individual accounts at any one time',
                        'Swap in different active users at any point',
                        'Inactive users retain read-only access to all their projects'
                    ]
                },
                student_monthly: {
                    price: 4.49,
                    cycle: 'month',
                    cycle_label: 'Monthly',
                    plan_label: 'Student Monthly',
                    checklist: [
                        'Access to a secure, editable, digital archive of every project',
                        'Add cues and notes anywhere on the script',
                        'Save and restore old versions of every project',
                        'Intuitive and streamlined real-time functionality',
                        'Indespensible for stage managers and all creative team members'
                    ]
                },
                student_yearly: {
                    price: 44,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Student Yearly',
                    checklist: [
                        'Access to a secure, editable, digital archive of every project',
                        'Add cues and notes anywhere on the script',
                        'Save and restore old versions of every project',
                        'Intuitive and streamlined real-time functionality',
                        'Indespensible for stage managers and all creative team members'
                    ]
                },
                showpass: {
                    price: 299,
                    cycle: 'one-time',
                    cycle_label: 'Show Pass',
                    plan_label: 'Show Pass',
                    checklist: [
                        'Unlimited collaborators can edit a single production',
                        'Add cues and notes anywhere on the script',
                        'Save and restore old versions of your production',
                        'Intuitive and streamlined real-time functionality',
                        'Indespensible for stage managers and all creative team members'
                    ]
                },
                dual_monthly: {
                    price: 19.98,
                    cycle: 'month',
                    cycle_label: 'Monthly',
                    plan_label: 'Cuelist + Notes List',
                    checklist: [
                        'Full Cuelist Collaborator access',
                        'Notes List authoring license (partner)',
                        'Sync projects with The Notes List',
                        'Deep links between Cuelist and Notes List',
                        'Students: apply Dual Student promo for $6.80/mo (no separate Student plan)'
                    ]
                },
                dual_yearly: {
                    price: 198,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Cuelist + Notes List',
                    checklist: [
                        'Full Cuelist Collaborator access',
                        'Notes List authoring license (partner)',
                        'Sync projects with The Notes List',
                        'Deep links between Cuelist and Notes List',
                        'Students: apply Dual Student promo for $68/yr (no separate Student plan)'
                    ]
                },
                dual_educational: {
                    price: 398,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Dual Educational',
                    checklist: [
                        'Cuelist + Notes List educational bundle',
                        '5 active seats included',
                        'Requires a .edu email address or equivalent',
                        'Additional Dual seats $59.99/seat/year'
                    ]
                },
                dual_institutional: {
                    price: 598,
                    cycle: 'year',
                    cycle_label: 'Annual',
                    plan_label: 'Dual Institutional',
                    checklist: [
                        'Cuelist + Notes List institutional bundle',
                        '5 active seats included',
                        'Additional Dual seats $59.99/seat/year',
                        'Seat holders must also be invited in Notes List'
                    ]
                }
            },
            doing: false,
            updateCardError: false,
            errorMessage: '',
            complete: false,
            number: false,
            expiry: false,
            cvc: false,
            postalcode: false,
            country: 'US',
            stripe: STRIPE_PUB_KEY,
            stripeOptions: {
                // see https://stripe.com/docs/stripe.js#element-options for details
            },
            options: {
                // see https://stripe.com/docs/stripe.js#element-options for details
            },
            institutionName: '',
            contactNumber: '',
            planToUpgrade: 'monthly',
            dualUpgradeInterval: 'month',
            dualUpgradeWorking: false,
            dualUpgradeError: '',
            bundleEligibility: {
                linked: false,
                licensed: false,
                managed_by: null,
                bundleDisabled: true,
                reason: 'loading'
            },
            eligibilityLoading: true,
            dynamicStripe: null,
            dynamicElements: null,
            dynamicCardNumber: null,
            dynamicCardExpiry: null,
            dynamicCardCvc: null,
            dynamicPostalCode: null
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'showMode',
            'project'
        ]),
        strings() {
            // Match server trial rule: only brand-new Stripe customers get FREE_TRIAL_DAYS.
            const hasStripeCustomer = !!(this.user && this.user.stripe_id);
            const date = hasStripeCustomer
                ? moment().format('MM/DD/YYYY')
                : moment().add(15, 'days').format('MM/DD/YYYY');
            return {
                ...this.plans[this.activePlan],
                tbd: date
            };
        },
        actionUrl() {
            // Use VUE_APP_CLOUD_FUNCTIONS_BASE in local so checkout hits the emulator
            // (where FREE_TRIAL_* from functions/.env is loaded), not deployed staging.
            return `${CLOUD_FUNCTIONS_BASE}/create-checkout-session`;
        },
        isIndividual() {
            return this.modalMode === 'individual';
        },
        isInstitutional() {
            return this.modalMode === 'institutional';
        },
        isShowPass() {
            return this.modalMode === 'showpass';
        },
        // ---- The student price (Josh's mock v2, approved 15 Oct 2026; lib/student-plan.js) ----
        isStudentMode() {
            return this.modalMode === 'student';
        },
        studentMonthlyPlanId() {
            return process.env.VUE_APP_STRIPE_STUDENT_MONTHLY_PLANID || '';
        },
        studentYearlyPlanId() {
            return process.env.VUE_APP_STRIPE_STUDENT_YEARLY_PLANID || '';
        },
        // Both test/live ids come from the frontend env; without them nothing about students shows.
        studentPlansConfigured() {
            return !!(this.studentMonthlyPlanId && this.studentYearlyPlanId);
        },
        studentState() {
            return studentWindow(this.user, new Date(), {
                month: this.studentMonthlyPlanId,
                year: this.studentYearlyPlanId
            });
        },
        isOnStudentPrice() {
            return this.studentState.state === 'current' && !this.isOnDual;
        },
        studentCurrentInterval() {
            return this.isOnStudentPrice ? this.studentState.interval : null;
        },
        // The window is over: refused at Checkout and by the switch, so say so before either.
        studentUnavailable() {
            return this.studentState.state === 'ended';
        },
        isStudentPlanSelected() {
            return this.activePlan === 'student_monthly' || this.activePlan === 'student_yearly';
        },
        studentCards() {
            return [
                { plan: 'student_monthly', interval: 'month', title: 'Cuelist Student Monthly', amount: '$4.49', per: '/month' },
                { plan: 'student_yearly', interval: 'year', title: 'Cuelist Student Yearly', amount: '$44', per: '/year' }
            ];
        },
        studentEndedText() {
            return endedBanner(this.studentState.endsAt, this.studentState.years);
        },
        studentReturningText() {
            return returningBanner(this.studentState.endsAt);
        },
        studentEndLineForSelected() {
            return endLine(this.studentState.endsAt, this.activePlan === 'student_yearly' ? 'year' : 'month');
        },
        studentEndLineForCurrent() {
            return endLine(this.studentState.endsAt, this.studentCurrentInterval);
        },
        // Team and Dual plans cannot switch to the student price (functions assertStudentSwitchAllowed).
        canSeeStudentFromSwitch() {
            return !!(this.studentPlansConfigured && !this.isOnDual && !this.isOnEduInstFamily);
        },
        // Extra lines in the confirm box: for any switch that leaves account credit, the credit and the first
        // charge after it (Josh, 15 Oct 2026); for a switch onto, or within, the student price, its end date.
        confirmExtraLines() {
            const a = this.pendingConfirmAction;
            if (!a) return [];
            const toStudent = a.type === 'student' || (a.type === 'cuelist_interval' && this.isOnStudentPrice);
            const lines = this.isTrialing ? [] : creditLines(this.switchPreview, { fromLabel: this.currentPlanLabel, toStudent });
            if (toStudent) lines.push(endLine(this.studentState.endsAt, a.interval));
            return lines;
        },
        // One-time Show Pass price id (test/live supplied via frontend env). Empty
        // string hides the Show Pass option entirely (same guard as account.vue).
        showPassPriceId() {
            return process.env.VUE_APP_STRIPE_SHOWPASSID || '';
        },
        portalActionUrl() {
            return `${CLOUD_FUNCTIONS_BASE}/create-portal-session`;
        },
        canUpgradeToDual() {
            return !!(
                this.isIndividualActivePlan &&
                !this.isOnDual &&
                !this.user.pending_cancel &&
                this.notesListLinked &&
                !this.bundleDisabled &&
                (this.dualMonthlyPlanId || this.dualYearlyPlanId)
            );
        },
        // Individual interval must match the Dual interval — matches the
        // symmetric guards added server-side in dual-upgrade.js.
        canUpgradeToDualMonthly() {
            return this.canUpgradeToDual && !this.isCurrentYearly;
        },
        canUpgradeToDualYearly() {
            return this.canUpgradeToDual && !this.isCurrentMonthly;
        },
        isOnDual() {
            return !!(
                this.user &&
                this.user.active_pay &&
                this.user.noteslist_integration_access &&
                this.user.noteslist_integration_source === 'dual'
            );
        },
        isOnDualIndividual() {
            if (!this.isOnDual) return false;
            const level = this.user.subscription_level || '';
            if (
                level === 'dual_educational' ||
                level === 'dual_institution' ||
                level === 'educational' ||
                level === 'institutional'
            ) {
                return false;
            }
            if (
                this.user.price_id &&
                (this.user.price_id === this.dualEducationalPlanId ||
                    this.user.price_id === this.resolvedDualInstitutionPlanId)
            ) {
                return false;
            }
            return (
                level === 'dual_individual' ||
                level === '' ||
                (!!this.user.price_id &&
                    (this.user.price_id === this.dualMonthlyPlanId ||
                        this.user.price_id === this.dualYearlyPlanId))
            );
        },
        currentDualLabel() {
            if (!this.isOnDual) return '';
            if (this.user.subscription_level === 'dual_educational') {
                return 'Cuelist + Notes List Educational';
            }
            if (this.user.subscription_level === 'dual_institution') {
                return 'Cuelist + Notes List Institutional';
            }
            if (this.currentDualInterval === 'year') {
                return 'Cuelist + Notes List Yearly';
            }
            if (this.currentDualInterval === 'month') {
                return 'Cuelist + Notes List Monthly';
            }
            return 'Cuelist + Notes List';
        },
        cancelPeriodEndDate() {
            const raw =
                (this.user && this.user.subscription_current_period_end) ||
                (this.user && this.user.noteslist_integration_period_end) ||
                (this.user && this.user.downgrade_at) ||
                this.cancelPeriodEndFetched ||
                null;
            if (!raw) return null;
            try {
                let m = null;
                if (raw.toDate) m = moment(raw.toDate());
                else if (raw.seconds) m = moment.unix(raw.seconds);
                else m = moment(raw);
                if (!m || !m.isValid()) return null;
                // Avoid treating the fallback phrase as a date
                if (typeof raw === 'string' && /billing period/i.test(raw)) return null;
                return m.format('MMMM D, YYYY');
            } catch (_e) {
                return null;
            }
        },
        partnerGrantConflictMessage() {
            if (!this.user || !this.user.noteslist_partner_grant_conflict) {
                return '';
            }
            return (
                this.user.noteslist_partner_grant_conflict_message ||
                'Your Notes List account already has an active subscription. Cuelist + Notes List team seats were not granted on Notes List. Resolve or cancel the personal Notes List plan, or contact support.'
            );
        },
        currentDualInterval() {
            if (!this.isOnDual) {
                return null;
            }
            if (this.user.price_id && this.user.price_id === this.dualYearlyPlanId) {
                return 'year';
            }
            if (this.user.price_id && this.user.price_id === this.dualMonthlyPlanId) {
                return 'month';
            }
            return null;
        },
        canSwitchDualInterval() {
            return !!(
                this.isOnDualIndividual &&
                !this.user.pending_downgrade &&
                this.dualMonthlyPlanId &&
                this.dualYearlyPlanId
            );
        },
        pendingConfirmLabel() {
            if (!this.pendingConfirmAction) return 'Confirm';
            if (
                this.pendingConfirmAction.type === 'student' &&
                !(this.dualUpgradeWorking || this.switchWorking || this.doing) &&
                this.switchPreview &&
                !(this.switchPreview.amount_due > 0)
            ) {
                return 'Confirm switch';
            }
            if (this.pendingConfirmAction.type === 'downgrade') {
                return this.switchWorking ? 'Scheduling…' : 'Schedule switch to Cuelist';
            }
            if (this.dualUpgradeWorking || this.switchWorking || this.doing) {
                return 'Working…';
            }
            const amount =
                this.switchPreview &&
                !this.switchPreview.deferred &&
                !this.switchPreview.same_price &&
                this.switchPreview.amount_due_display;
            return amount ? `Confirm — charge $${amount}` : 'Confirm switch';
        },
        isPendingCuelistMonthly() {
            const a = this.pendingConfirmAction;
            return !!(
                a &&
                (a.type === 'cuelist_interval' || a.type === 'downgrade') &&
                a.interval === 'month'
            );
        },
        isPendingCuelistYearly() {
            const a = this.pendingConfirmAction;
            return !!(
                a &&
                (a.type === 'cuelist_interval' || a.type === 'downgrade') &&
                a.interval === 'year'
            );
        },
        isPendingDualMonthly() {
            const a = this.pendingConfirmAction;
            return !!(a && a.type === 'upgrade_dual' && a.interval === 'month');
        },
        isPendingDualYearly() {
            const a = this.pendingConfirmAction;
            return !!(a && a.type === 'upgrade_dual' && a.interval === 'year');
        },
        isPendingEducational() {
            const a = this.pendingConfirmAction;
            return !!(a && a.type === 'team_upgrade' && a.target === 'educational');
        },
        isPendingInstitutional() {
            const a = this.pendingConfirmAction;
            return !!(a && a.type === 'team_upgrade' && a.target === 'institutional');
        },
        isPendingDualEducational() {
            const a = this.pendingConfirmAction;
            return !!(a && a.type === 'team_upgrade' && a.target === 'dual_educational');
        },
        isPendingDualInstitutional() {
            const a = this.pendingConfirmAction;
            return !!(a && a.type === 'team_upgrade' && a.target === 'dual_institution');
        },
        pendingConfirmSummary() {
            const a = this.pendingConfirmAction;
            if (a && (a.type === 'student' || (a.type === 'cuelist_interval' && this.isOnStudentPrice))) {
                return a.interval === 'year' ? 'Cuelist Student Yearly, $44/year' : 'Cuelist Student Monthly, $4.49/month';
            }
            if (this.isPendingCuelistMonthly) return 'Cuelist Monthly — $12.99/month';
            if (this.isPendingCuelistYearly) return 'Cuelist Yearly — $129/year';
            if (this.isPendingDualMonthly) return 'Cuelist + Notes List Monthly — $19.98/month';
            if (this.isPendingDualYearly) return 'Cuelist + Notes List Yearly — $198/year';
            if (this.isPendingEducational) return 'Educational Plan — $249/year';
            if (this.isPendingInstitutional) return 'Institutional Plan — $349/year';
            if (this.isPendingDualEducational) return 'Cuelist + Notes List Educational — $398/year';
            if (this.isPendingDualInstitutional) return 'Cuelist + Notes List Institutional — $598/year';
            return '';
        },
        isTrialing() {
            if (this.switchPreview && typeof this.switchPreview.trialing === 'boolean') {
                return this.switchPreview.trialing;
            }
            return !!(this.user && this.user.subscription_status === 'trialing');
        },
        trialEndDate() {
            const raw =
                (this.switchPreview && this.switchPreview.trial_end) ||
                (this.user && this.user.subscription_trial_end);
            if (!raw) return null;
            try {
                const m = moment(raw);
                return m.isValid() ? m.format('MMMM D, YYYY') : null;
            } catch (_e) {
                return null;
            }
        },
        bundleDisabled() {
            return !!(
                this.bundleEligibility &&
                this.bundleEligibility.bundleDisabled
            );
        },
        notesListLinked() {
            return !!(this.user && this.user.noteslist_user_id);
        },
        showDualAlreadyLicensedBanner() {
            return !!(
                this.notesListLinked &&
                this.bundleEligibility &&
                this.bundleEligibility.licensed &&
                this.bundleEligibility.managed_by !== 'cuelist'
            );
        },
        dualEligibleAtDate() {
            const raw = this.bundleEligibility && this.bundleEligibility.current_period_end;
            if (!raw) return null;
            try {
                const m = moment(raw);
                return m.isValid() ? m.format('MMMM D, YYYY') : null;
            } catch (_e) {
                return null;
            }
        },
        showDualEligibilityLoader() {
            // Linked account: wait for Notes List licence check before enabling Dual boxes
            return this.eligibilityLoading && this.notesListLinked;
        },
        dualPlansLocked() {
            return this.showDualEligibilityLoader || this.bundleDisabled;
        },
        dualEduUnavailable() {
            return (
                this.dualPlansLocked ||
                !this.hasEduEmail ||
                !this.notesListLinked
            );
        },
        dualInstUnavailable() {
            return this.dualPlansLocked || !this.notesListLinked;
        },
        planModalState() {
            // States 1–3 for unpaid individual modal
            if (this.user && this.user.active_pay) {
                return 4;
            }
            if (this.eligibilityLoading) {
                return this.notesListLinked ? 0 : 1;
            }
            if (!this.bundleEligibility || this.bundleEligibility.reason === 'loading') {
                return this.notesListLinked ? 0 : 1;
            }
            if (this.bundleEligibility.reason === 'not_linked' || !this.bundleEligibility.linked) {
                return 1;
            }
            if (
                this.bundleEligibility.licensed &&
                this.bundleEligibility.managed_by !== 'cuelist'
            ) {
                return 2;
            }
            return 3;
        },
        isIndividualActivePlan() {
            if (!this.user || !this.user.active_pay) return false;
            if (this.isOnDual) return false;
            const level = this.user.subscription_level || '';
            return (
                level === 'monthly' ||
                level === 'yearly' ||
                // The student rate is an individual plan: same Manage Plan family, interval switch stays on student.
                level === 'student' ||
                level === 'collaborator' ||
                level === '' ||
                this.isCurrentMonthly ||
                this.isCurrentYearly
            );
        },
        isCurrentEducational() {
            return !!(this.user && this.user.subscription_level === 'educational' && !this.isOnDual);
        },
        isCurrentInstitutional() {
            return !!(this.user && this.user.subscription_level === 'institutional' && !this.isOnDual);
        },
        isCurrentDualEducational() {
            return !!(
                this.isOnDual &&
                (this.user.subscription_level === 'dual_educational' ||
                    (this.user.price_id && this.user.price_id === this.dualEducationalPlanId))
            );
        },
        isCurrentDualInstitutional() {
            return !!(
                this.isOnDual &&
                (this.user.subscription_level === 'dual_institution' ||
                    (this.user.price_id &&
                        this.user.price_id === this.resolvedDualInstitutionPlanId))
            );
        },
        isOnEduInstFamily() {
            return !!(
                this.isCurrentEducational ||
                this.isCurrentInstitutional ||
                this.isCurrentDualEducational ||
                this.isCurrentDualInstitutional
            );
        },
        // Cuelist-only Individual (or Edu↔Inst) can upgrade mid-cycle without Notes List
        canUpgradeToCuelistTeamBase() {
            if (!this.user || !this.user.active_pay || this.isOnDual || this.user.pending_cancel) return false;
            return !!(
                this.isIndividualActivePlan ||
                this.isCurrentEducational ||
                this.isCurrentInstitutional
            );
        },
        canUpgradeToEducational() {
            return (
                this.canUpgradeToCuelistTeamBase &&
                !this.isCurrentEducational &&
                this.hasEduEmail
            );
        },
        canUpgradeToInstitutional() {
            return this.canUpgradeToCuelistTeamBase && !this.isCurrentInstitutional;
        },
        // Mirrors currentTeamPlanFamily in switch-plan-billing.js — keep in sync.
        subscriptionFamily() {
            if (!this.user) return 'individual';
            const level = this.user.subscription_level || '';
            if (this.isOnDual) {
                if (level === 'dual_educational') return 'dual_educational';
                if (level === 'dual_institution') return 'dual_institution';
                return 'dual_individual';
            }
            if (level === 'educational') return 'educational';
            if (level === 'institutional') return 'institutional';
            return 'individual';
        },
        // Confirmed matrix (2026-08-17): individual/dual-individual (either
        // interval) can reach either Dual team tier; Educational only
        // reaches Dual Educational; Institutional only reaches Dual
        // Institutional; never cross.
        canUpgradeToDualEducational() {
            if (!this.user || !this.user.active_pay || this.user.pending_cancel || this.isCurrentDualEducational) return false;
            if (!this.notesListLinked || !this.dualEducationalPlanId || this.dualPlansLocked) return false;
            return (
                this.subscriptionFamily === 'individual' ||
                this.subscriptionFamily === 'dual_individual' ||
                this.subscriptionFamily === 'educational'
            );
        },
        canUpgradeToDualInstitutional() {
            if (!this.user || !this.user.active_pay || this.user.pending_cancel || this.isCurrentDualInstitutional) return false;
            if (!this.notesListLinked || !this.resolvedDualInstitutionPlanId || this.dualPlansLocked) return false;
            return (
                this.subscriptionFamily === 'individual' ||
                this.subscriptionFamily === 'dual_individual' ||
                this.subscriptionFamily === 'institutional'
            );
        },
        isCurrentMonthly() {
            if (!this.user || this.isOnDual) return false;
            const monthlyId = process.env.VUE_APP_STRIPE_MONTHLTPLANID;
            if (this.user.price_id && monthlyId && this.user.price_id === monthlyId) {
                return true;
            }
            const studentMonthlyId = process.env.VUE_APP_STRIPE_STUDENT_MONTHLY_PLANID;
            if (this.user.price_id && studentMonthlyId && this.user.price_id === studentMonthlyId) {
                return true;
            }
            const level = this.user.subscription_level || '';
            return level === 'monthly' || level === 'collaborator';
        },
        isCurrentYearlyByPriceOnly() {
            const yearlyId = process.env.VUE_APP_STRIPE_YRARLYPLANID;
            const studentYearlyId = process.env.VUE_APP_STRIPE_STUDENT_YEARLY_PLANID;
            return !!(
                this.user &&
                this.user.price_id &&
                ((yearlyId && this.user.price_id === yearlyId) ||
                    (studentYearlyId && this.user.price_id === studentYearlyId))
            );
        },
        isCurrentYearly() {
            if (!this.user || this.isOnDual) return false;
            if (this.isCurrentYearlyByPriceOnly) return true;
            return (this.user.subscription_level || '') === 'yearly';
        },
        currentPlanLabel() {
            if (this.isOnDual) return 'Cuelist + Notes List';
            if (this.isOnStudentPrice) {
                return this.studentCurrentInterval === 'year' ? 'Cuelist Student Yearly' : 'Cuelist Student Monthly';
            }
            if (this.isCurrentEducational) return 'Educational Plan';
            if (this.isCurrentInstitutional) return 'Institutional Plan';
            if (this.isCurrentYearly) return 'Cuelist Yearly';
            if (this.isCurrentMonthly) return 'Cuelist Monthly';
            return 'Cuelist';
        },
        isDualPlanSelected() {
            return (
                this.activePlan === 'dual_monthly' ||
                this.activePlan === 'dual_yearly' ||
                this.activePlan === 'dual_educational' ||
                this.activePlan === 'dual_institutional'
            );
        },
        // Prefer env; if Dual Edu is wired but Inst ID missed a webpack restart, still show the 4th card
        resolvedDualInstitutionPlanId() {
            if (this.dualInstitutionPlanId) {
                return this.dualInstitutionPlanId;
            }
            // Same minted test price as CueList-v2/.env — keeps 4th box visible after HMR without full restart
            if (this.dualEducationalPlanId) {
                return 'price_1U0c9aE8UWhSxHj0EYFXJnOf';
            }
            return '';
        },
        hasEduEmail() {
            // Educational/Student pricing is advertised as .edu-gated but
            // deliberately NOT enforced (Josh, 2026-08-xx): high school and
            // international students often lack a .edu address, and he'd
            // rather accept some non-students at the discounted rate than
            // block real students who can't prove eligibility. Do not
            // reintroduce a domain check here without checking with him first.
            return true;
        },
        canSubmitCheckout() {
            if (!this.termsandpolicy) {
                return false;
            }
            if (this.isStudentPlanSelected && (!this.studentPlansConfigured || this.studentUnavailable)) {
                return false;
            }
            if (
                (this.activePlan === 'educational' ||
                    this.activePlan === 'dual_educational') &&
                !this.hasEduEmail
            ) {
                return false;
            }
            if (
                (this.activePlan === 'dual_monthly' ||
                    this.activePlan === 'dual_yearly' ||
                    this.activePlan === 'dual_educational' ||
                    this.activePlan === 'dual_institutional') &&
                this.dualPlansLocked
            ) {
                return false;
            }
            return true;
        }
    },
    watch: {
        number () { this.update(); },
        expiry () { this.update(); },
        cvc () { this.update(); },
        postalcode () { this.update(); }
    },
    methods: {
        open(options) {
            if (options && options.mode) {
                this.openMode = options.mode;
            }
            if (options && options.preferDual && !this.bundleDisabled) {
                if (this.dualMonthlyPlanId) {
                    this.dualUpgradeInterval = 'month';
                    this.activePlan = 'dual_monthly';
                    this.activePlanId = this.dualMonthlyPlanId;
                } else if (this.dualYearlyPlanId) {
                    this.dualUpgradeInterval = 'year';
                    this.activePlan = 'dual_yearly';
                    this.activePlanId = this.dualYearlyPlanId;
                }
            }
            this.$refs.upgradeAcct.open();
            this.refreshBundleEligibility();
        },
        async refreshBundleEligibility() {
            this.eligibilityLoading = true;
            try {
                this.bundleEligibility = await fetchBundleEligibility();
                if (
                    this.bundleDisabled &&
                    (this.activePlan === 'dual_monthly' ||
                        this.activePlan === 'dual_yearly' ||
                        this.activePlan === 'dual_educational' ||
                        this.activePlan === 'dual_institutional')
                ) {
                    this.setActivePlan(this.isIndividual ? 'monthly' : 'educational');
                }
            } finally {
                this.eligibilityLoading = false;
            }
        },
        goConnectNotesList() {
            this.closeDialog();
            this.$router.push({ name: 'account' }).catch(() => {});
        },
        submitCheckout() {
            // Re-assert env plan id right before POST so a stale activePlanId
            // (e.g. legacy Daily test price) cannot reach create-checkout-session.
            this.setActivePlan(this.activePlan);
            if (
                this.activePlan === 'monthly' &&
                this.activePlanId !== process.env.VUE_APP_STRIPE_MONTHLTPLANID
            ) {
                this.activePlanId = process.env.VUE_APP_STRIPE_MONTHLTPLANID;
            }
            if (this.activePlan === 'educational' && !this.hasEduEmail) {
                return;
            }
            if (this.activePlan === 'dual_educational' && !this.hasEduEmail) {
                return;
            }
            stashPendingPlanUpdate(this.user);
            this.$nextTick(() => {
                if (this.$refs.checkoutForm) {
                    this.$refs.checkoutForm.submit();
                }
            });
        },
        closeDialog() {
            this.$refs.upgradeAcct.close();
        },
        formatDowngradeAt(value) {
            if (!value) return 'end of billing period';
            try {
                if (value.toDate) return moment(value.toDate()).format('MM/DD/YYYY');
                if (value.seconds) return moment.unix(value.seconds).format('MM/DD/YYYY');
                return moment(value).format('MM/DD/YYYY');
            } catch (_e) {
                return 'end of billing period';
            }
        },
        async selectDualUpgrade(interval) {
            this.dualUpgradeInterval = interval;
            this.pendingConfirmAction = { type: 'upgrade_dual', interval };
            this.switchError = '';
            this.switchWorking = true;
            try {
                this.switchPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target: 'dual',
                    interval
                });
            } catch (err) {
                this.switchPreview = null;
                this.switchError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.switchWorking = false;
            }
        },
        async selectDowngradeTarget(interval) {
            this.pendingConfirmAction = { type: 'downgrade', interval };
            this.switchError = '';
            this.switchWorking = true;
            try {
                this.switchPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target: 'individual',
                    interval
                });
            } catch (err) {
                this.switchPreview = null;
                this.switchError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.switchWorking = false;
            }
        },
        async confirmUpgradeToDual() {
            if (!this.canUpgradeToDual && !this.isOnDual) return;
            if (!this.dualUpgradeInterval) {
                this.dualUpgradeInterval = this.isCurrentYearly ? 'year' : 'month';
            }
            this.pendingConfirmAction = {
                type: 'upgrade_dual',
                interval: this.dualUpgradeInterval
            };
            await this.runPendingConfirmAction();
        },
        // Which button a student card gets in the Switch view: 'upgrade' / 'interval' (a student changing interval,
        // stays on the student price), 'switch' (full price down to student), or null (Unavailable).
        studentCardAction(card) {
            if (!this.user || !this.user.active_pay || this.user.pending_cancel || this.isOnDual) return null;
            if (this.isOnStudentPrice) {
                if (card.interval === this.studentCurrentInterval) return null;
                return card.interval === 'year' ? 'upgrade' : 'interval';
            }
            if (this.studentUnavailable || !this.isIndividualActivePlan || this.isOnEduInstFamily) return null;
            return 'switch';
        },
        isPendingStudent(interval) {
            const a = this.pendingConfirmAction;
            if (!a || a.interval !== interval) return false;
            return a.type === 'student' || (a.type === 'cuelist_interval' && this.isOnStudentPrice);
        },
        async selectStudentSwitch(interval) {
            if (this.isOnDual || this.doing) return;
            const resolvedInterval = interval === 'year' ? 'year' : 'month';
            this.pendingConfirmAction = { type: 'student', interval: resolvedInterval };
            this.switchError = '';
            this.switchWorking = true;
            try {
                this.switchPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target: 'student',
                    interval: resolvedInterval
                });
            } catch (err) {
                this.switchPreview = null;
                this.switchError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.switchWorking = false;
            }
        },
        // The server writes the plan (and the webhook the 4-year clock); the user binding shows it.
        async applyStudentSwitch(interval) {
            this.doing = true;
            this.switchError = '';
            try {
                let result = await this.$store.dispatch('switchToStudentRate', {
                    interval: interval === 'year' ? 'year' : 'month'
                });
                if (result && result.mode === 'requires_action') {
                    result = await this.handleRequiresAction(result.client_secret);
                    if (!result) return;
                }
                this.$toast.success('Plan updated.');
                this.pendingConfirmAction = null;
                this.switchPreview = null;
                this.closeDialog();
            } catch (err) {
                this.switchError =
                    (err && err.message) ||
                    (err && err.body && err.body.message) ||
                    'Could not switch plan.';
            } finally {
                this.doing = false;
            }
        },
        async selectCuelistIntervalSwitch(interval) {
            // Month ↔ year for Cuelist-only via Cloud Functions (active Stripe prices).
            // Preview first so the user sees the exact prorated charge before confirming.
            if (this.isOnDual || this.doing) return;
            const resolvedInterval = interval === 'year' ? 'year' : 'month';
            this.pendingConfirmAction = { type: 'cuelist_interval', interval: resolvedInterval };
            this.switchError = '';
            this.switchWorking = true;
            try {
                this.switchPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target: 'individual',
                    interval: resolvedInterval
                });
            } catch (err) {
                this.switchPreview = null;
                this.switchError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.switchWorking = false;
            }
        },
        /**
         * A switch can come back requiring an interactive SCA/3D-Secure
         * challenge (common for India-billed cards, not exclusive to them) —
         * this is NOT a decline, the same card just needs one more step.
         * Runs the Stripe.js challenge, then asks the backend to finalize
         * once it's done. Returns the confirm response on success, or null
         * (with switchError already set) if the customer didn't complete it.
         */
        async handleRequiresAction(clientSecret) {
            if (!clientSecret) {
                this.switchError =
                    'Payment verification could not start. Please refresh the page and try again.';
                return null;
            }
            if (!(await ensureStripeJsLoaded())) {
                this.switchError =
                    'Payment verification could not start. Please check your connection and try again.';
                return null;
            }
            const stripeJs = window.Stripe(this.stripe);
            const confirmResult = await stripeJs.confirmCardPayment(clientSecret);
            if (confirmResult.error) {
                this.switchError =
                    confirmResult.error.message || 'Payment verification failed. Please try again.';
                return null;
            }
            try {
                return await this.$store.dispatch('confirmNotesListSwitch');
            } catch (err) {
                this.switchError =
                    (err && err.message) ||
                    'Verification completed, but we could not finish the switch. Please try again.';
                return null;
            }
        },
        async applyCuelistIntervalSwitch(interval) {
            this.doing = true;
            this.switchError = '';
            try {
                let result = await this.$store.dispatch(
                    'switchCuelistIndividualInterval',
                    { interval: interval === 'year' ? 'year' : 'month' }
                );
                if (result && result.mode === 'requires_action') {
                    result = await this.handleRequiresAction(result.client_secret);
                    if (!result) return;
                }
                // A student's interval switch stays on the student price (server), so its level stays 'student'.
                let level = interval === 'year' ? 'yearly' : 'monthly';
                if (this.isOnStudentPrice) level = 'student';
                const update = { subscription_level: level };
                if (result && result.price_id) {
                    update.price_id = result.price_id;
                }
                if (result && result.current_period_end) {
                    update.subscription_current_period_end = result.current_period_end;
                }
                await this.$store.dispatch('updateUser', {
                    uid: this.user.uid,
                    update
                });
                this.$toast.success('Plan updated.');
                this.pendingConfirmAction = null;
                this.switchPreview = null;
                this.closeDialog();
            } catch (err) {
                this.switchError =
                    (err && err.message) ||
                    (err && err.body && err.body.message) ||
                    'Could not switch plan.';
            } finally {
                this.doing = false;
            }
        },
        openManageBilling() {
            if (!this.user || !this.user.stripe_id) {
                this.switchError = 'No Stripe customer found for this account.';
                return;
            }
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = this.portalActionUrl;
            form.target = '_blank';
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'customer_id';
            input.value = this.user.stripe_id;
            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        },
        async selectTeamUpgrade(target) {
            const isDualTarget = target === 'dual_educational' || target === 'dual_institution';
            if (this.switchWorking) return;
            // Dual users can only reach the two Dual team targets — not plain
            // Educational/Institutional (that means leaving Dual first).
            if (this.isOnDual && !isDualTarget) return;
            if (target === 'educational' && !this.hasEduEmail) {
                this.switchError =
                    'Educational pricing requires a .edu email address or equivalent on your account.';
                return;
            }
            if (
                target !== 'educational' &&
                target !== 'institutional' &&
                target !== 'dual_educational' &&
                target !== 'dual_institution'
            ) {
                return;
            }
            this.pendingConfirmAction = { type: 'team_upgrade', target };
            this.switchWorking = true;
            this.switchError = '';
            try {
                this.switchPreview = await this.$store.dispatch('previewNotesListSwitch', {
                    target,
                    interval: 'year'
                });
            } catch (err) {
                this.switchPreview = null;
                this.switchError = (err && err.message) || 'Could not preview switch.';
            } finally {
                this.switchWorking = false;
            }
        },
        async applyTeamUpgrade(target) {
            this.switchWorking = true;
            this.switchError = '';
            try {
                let result = await this.$store.dispatch('applyNotesListPlanSwitch', {
                    target
                });
                if (result && result.mode === 'requires_action') {
                    result = await this.handleRequiresAction(result.client_secret);
                    if (!result) return;
                }
                const teamUpgradeLabels = {
                    educational: 'Upgraded to Educational Plan.',
                    institutional: 'Upgraded to Institutional Plan.',
                    dual_educational: 'Upgraded to Cuelist + Notes List Educational.',
                    dual_institution: 'Upgraded to Cuelist + Notes List Institutional.'
                };
                this.$toast.success(teamUpgradeLabels[target] || 'Plan updated.');
                this.pendingConfirmAction = null;
                this.switchPreview = null;
                this.closeDialog();
                if (target === 'dual_educational' || target === 'dual_institution') {
                    await this.maybeResyncLinkedProjectAfterUpgrade();
                }
            } catch (err) {
                this.switchError =
                    (err && err.message) || 'Could not upgrade plan. Please try again.';
            } finally {
                this.switchWorking = false;
            }
        },
        confirmCancelSubscription() {
            this.unsubscribePlan(true);
        },
        async openCancelConfirm() {
            this.cancelPeriodEndFetched = null;
            this.showCancelConfirm = true;
            this.switchError = '';
            // Always load Stripe current_period_end (works for Edu/Inst/Individual/Dual)
            try {
                const result = await this.$store.dispatch(
                    'fetchNotesListSubscriptionPeriodEnd'
                );
                if (result && result.current_period_end) {
                    this.cancelPeriodEndFetched = result.current_period_end;
                }
            } catch (_err) {
                // Copy falls back without a concrete date
            }
        },
        async runPendingConfirmAction() {
            if (!this.pendingConfirmAction) return;
            const { type, interval, target } = this.pendingConfirmAction;
            this.switchError = '';
            if (type === 'downgrade') {
                this.switchWorking = true;
                try {
                    const result = await this.$store.dispatch('downgradeNotesListFromDual', {
                        interval
                    });
                    this.$toast.success(
                        (result && result.message) ||
                            'Switch to Cuelist Individual scheduled for end of billing period.'
                    );
                    this.pendingConfirmAction = null;
                    this.switchPreview = null;
                    this.closeDialog();
                } catch (err) {
                    this.switchError = (err && err.message) || 'Could not schedule downgrade.';
                } finally {
                    this.switchWorking = false;
                }
                return;
            }
            if (type === 'cuelist_interval') {
                await this.applyCuelistIntervalSwitch(interval);
                return;
            }
            if (type === 'student') {
                await this.applyStudentSwitch(interval);
                return;
            }
            if (type === 'team_upgrade') {
                await this.applyTeamUpgrade(target);
                return;
            }
            this.dualUpgradeInterval = interval;
            const ok = await this.upgradeToDualPlan();
            if (ok) {
                this.pendingConfirmAction = null;
                this.switchPreview = null;
            }
        },
        isPlanActionBusy(type, value) {
            if (!(this.switchWorking || this.dualUpgradeWorking)) {
                return false;
            }
            const action = this.pendingConfirmAction;
            if (!action || action.type !== type) {
                return false;
            }
            if (type === 'team_upgrade') {
                return action.target === value;
            }
            return action.interval === value;
        },
        clearPendingSwitch() {
            this.pendingConfirmAction = null;
            this.switchPreview = null;
            this.switchError = '';
        },
        async cancelScheduledDowngrade() {
            this.switchWorking = true;
            this.switchError = '';
            try {
                await this.$store.dispatch('cancelNotesListPendingDowngrade');
                this.$toast.success('Scheduled switch canceled — you stay on Dual.');
            } catch (err) {
                this.switchError = (err && err.message) || 'Could not cancel scheduled switch.';
            } finally {
                this.switchWorking = false;
            }
        },
        async mountStripeElements() {
            if (!(await ensureStripeJsLoaded())) {
                this.errorMessage = 'Payment form failed to load. Please check your connection and try again.';
                return;
            }
            this.dynamicStripe = window.Stripe(this.stripe);
            this.dynamicElements = this.dynamicStripe.elements();
            this.dynamicCardNumber = this.dynamicElements.create('cardNumber');
            this.dynamicCardExpiry = this.dynamicElements.create('cardExpiry');
            this.dynamicCardCvc = this.dynamicElements.create('cardCvc');
            this.dynamicPostalCode = this.dynamicElements.create('postalCode');
            this.dynamicCardNumber.on('change', ($event) => {
                this.number = $event.complete;
            });
            this.dynamicCardExpiry.on('change', ($event) => {
                this.expiry = $event.complete;
            });
            this.dynamicCardCvc.on('change', ($event) => {
                this.cvc = $event.complete;
            });
            this.dynamicPostalCode.on('change', ($event) => {
                this.postalcode = $event.complete;
            });

            this.dynamicCardNumber.mount(this.$refs.cardNumber);
            this.dynamicCardExpiry.mount(this.$refs.cardExpiry);
            this.dynamicCardCvc.mount(this.$refs.cardCvc);
            this.dynamicPostalCode.mount(this.$refs.postalCode);
        },
        unmountStripeElements() {
            if (!this.dynamicCardNumber) return;

            this.dynamicCardNumber.unmount(this.$refs.cardNumber);
            this.dynamicCardExpiry.unmount(this.$refs.cardExpiry);
            this.dynamicCardCvc.unmount(this.$refs.cardCvc);
            this.dynamicPostalCode.unmount(this.$refs.postalCode);

            this.dynamicCardNumber.destroy();
            this.dynamicCardExpiry.destroy();
            this.dynamicCardCvc.destroy();
            this.dynamicPostalCode.destroy();

            this.dynamicCardNumber = null;
            this.dynamicCardExpiry = null;
            this.dynamicCardCvc = null;
            this.dynamicPostalCode = null;
            this.dynamicElements = null;
            this.dynamicStripe = null;
        },
        fixContent() {
            // Open on the plan family the customer is actually on. An educational
            // or institutional subscriber used to land on the INDIVIDUAL list with
            // all four cards greyed out by the isOnEduInstFamily guard, and had to
            // notice a small text link to reach their own plan. Manage Billing sits
            // on the current-plan card, so this also hid the route to invoices and
            // billing history.
            //
            // Routed through setModalMode rather than assigning modalMode directly,
            // because the mode and its default plan + price id must move together:
            // seeding the mode alone leaves the institutional view with NO card
            // selected and the monthly price id still armed for submit.
            //
            // ⚠ Seeded from isOnEduInstFamily, which reads the user's OWN plan.
            // Never from isShowPass / isIndividual / isInstitutional — those are
            // computed FROM modalMode, so seeding off them is circular and would
            // only ever repeat whichever mode happened to be open last.
            //
            // Show Pass is deliberately not seeded: that mode is the buy-a-pass
            // view, and someone who already holds one does not want to land there.
            // A student lands on the student view (mock v2, D); open({ mode }) wins for that one open.
            let mode = this.isOnEduInstFamily ? 'institutional' : 'individual';
            if (this.isOnStudentPrice && this.studentPlansConfigured) mode = 'student';
            if (this.openMode === 'student' && !this.studentPlansConfigured) this.openMode = null;
            this.setModalMode(this.openMode || mode);
            this.openMode = null;
            this.termsandpolicy = false;
            this.promocode = '';
            this.promoApplied = false;
            this.promoError = false;
            this.promoTimer = null;
            this.promoData = null;
            this.doing = false;
            this.updateCardError = false;
            this.errorMessage = '';
            this.complete = false;
            this.number = false;
            this.expiry = false;
            this.cvc = false;
            this.postalcode = false;
            this.country = 'US';
            this.institutionName = '';
            this.contactNumber = '';
            this.dualUpgradeError = '';
            this.dualUpgradeWorking = false;
            this.showCancelConfirm = false;
            this.cancelPeriodEndFetched = null;
            this.switchPreview = null;
            this.switchError = '';
            this.pendingConfirmAction = null;
            // Open Edu/Inst tab when the subscriber is already on that plan family
            const level = (this.user && this.user.subscription_level) || '';
            if (
                level === 'educational' ||
                level === 'institutional' ||
                level === 'dual_educational' ||
                level === 'dual_institution'
            ) {
                this.isIndividual = false;
            } else {
                this.isIndividual = true;
            }
            if (this.dualMonthlyPlanId) {
                this.dualUpgradeInterval = 'month';
            } else if (this.dualYearlyPlanId) {
                this.dualUpgradeInterval = 'year';
            }
            Vue.material.setCurrentTheme(this.showMode ? 'show-mode' : 'default');

            if (!this.user.active_pay) {
                this.mountStripeElements();
            }
            this.refreshBundleEligibility();
        },
        startOver() {
            this.showCancelConfirm = false;
            Vue.material.setCurrentTheme(this.showMode ? 'show-mode' : 'default');

            if (this.dynamicCardNumber) {
                this.unmountStripeElements();
            }
        },
        cancel() {
            this.$refs.upgradeAcct.close();
        },
        // staging's markup toggles individual <-> institutional through this name;
        // sandbox generalised the state to three modes, so keep it as an alias
        // rather than re-authoring staging's markup.
        toggleModalType() {
            this.setModalMode(this.isIndividual ? 'institutional' : 'individual');
        },
        setModalMode(mode) {
            this.modalMode = mode;
            this.promocode = '';
            this.promoApplied = false;
            this.promoError = false;
            this.promoTimer = null;
            this.promoData = null;
            if (mode === 'individual') {
                this.activePlan = 'monthly';
                this.activePlanId = `${process.env.VUE_APP_STRIPE_MONTHLTPLANID}`;
            } else if (mode === 'institutional') {
                this.activePlan = 'educational';
                this.activePlanId = `${process.env.VUE_APP_STRIPE_EDUPLANID}`;
            } else if (mode === 'showpass') {
                this.activePlan = 'showpass';
                this.activePlanId = this.showPassPriceId;
            } else if (mode === 'student') {
                this.activePlan = 'student_monthly';
                this.activePlanId = this.studentMonthlyPlanId;
            }
            this.clearPendingSwitch();
        },
        setActivePlan(plan) {
            if (this.activePlan !== plan) {
                this.activePlan = plan;
            }
            switch (plan) {
            case 'monthly':
                this.activePlanId = `${process.env.VUE_APP_STRIPE_MONTHLTPLANID}`;
                break;
            case 'yearly':
                this.activePlanId = `${process.env.VUE_APP_STRIPE_YRARLYPLANID}`;
                break;
            case 'educational':
                this.activePlanId = `${process.env.VUE_APP_STRIPE_EDUPLANID}`;
                break;
            case 'institutional':
                this.activePlanId = `${process.env.VUE_APP_STRIPE_INSTPLANID}`;
                break;
            case 'dual_monthly':
                this.activePlanId = this.dualMonthlyPlanId;
                break;
            case 'dual_yearly':
                this.activePlanId = this.dualYearlyPlanId;
                break;
            case 'dual_educational':
                this.activePlanId = this.dualEducationalPlanId;
                break;
            case 'dual_institutional':
                this.activePlanId = this.resolvedDualInstitutionPlanId;
                break;
            case 'showpass':
                this.activePlanId = this.showPassPriceId;
                break;
            case 'student_monthly':
                this.activePlanId = this.studentMonthlyPlanId;
                break;
            case 'student_yearly':
                this.activePlanId = this.studentYearlyPlanId;
                break;
            default:
                break;
            }
        },
        unsubscribePlan(skipBrowserConfirm) {
            if (!skipBrowserConfirm) {
                // eslint-disable-next-line
                if (!confirm('Do you really want to cancel your current subscription?')) return;
            }
            this.doing = true;
            this.switchError = '';
            // Schedules cancellation for end of the current billing period — access
            // is kept until then (matches the copy shown in the cancel confirm box).
            // Actual revocation happens later via the Stripe webhook when the
            // subscription is finally canceled.
            Vue.http.post(CANCEL_PLAN_URL, {
                customer_id: this.user.stripe_id,
                subscription_id: this.user.subscription_id
            }).then(async (response) => {
                const periodEnd = response.body && response.body.current_period_end;
                const update = { pending_cancel: true };
                if (periodEnd) {
                    update.subscription_current_period_end = new Date(periodEnd * 1000).toISOString();
                }
                await this.$store.dispatch('updateUser', {
                    uid: this.user.uid,
                    update
                });
                this.$toast.success(
                    'Your subscription is scheduled to cancel at the end of the billing period. You keep access until then.'
                );
                this.doing = false;
                this.showCancelConfirm = false;
                this.closeDialog();
            }).catch((err) => {
                this.switchError =
                    (err && err.body && err.body.error) || (err && err.message) || 'Could not cancel subscription.';
                this.doing = false;
            });
        },
        async keepScheduledCuelistCancellation() {
            this.doing = true;
            this.switchError = '';
            try {
                await Vue.http.post(CANCEL_PLAN_UNDO_URL, {
                    customer_id: this.user.stripe_id,
                    subscription_id: this.user.subscription_id
                });
                await this.$store.dispatch('updateUser', {
                    uid: this.user.uid,
                    update: { pending_cancel: false }
                });
                this.$toast.success('Scheduled cancellation undone — your subscription continues as usual.');
            } catch (err) {
                this.switchError =
                    (err && err.body && err.body.error) || (err && err.message) || 'Could not undo cancellation.';
            } finally {
                this.doing = false;
            }
        },
        updateCard() {
            this.doing = true;
            this.updateCardError = false;
            this.errorMessage = '';
            this.dynamicStripe.createToken(this.dynamicCardNumber).then((data) => {
                Vue.http.post(UPDATE_CARD_URL, {
                    token: data.token,
                    customer_id: this.user.stripe_id || null,
                    customer: {
                        uid: this.user.uid,
                        name: this.user.username,
                        email: this.user.email
                    }
                }).then(() => {
                    this.closeDialog();
                    this.doing = false;
                    this.completed = true;
                }).catch((err) => {
                    this.updateCardError = true;
                    this.errorMessage = (err.body && err.body.error) || err.body.message;
                    this.doing = false;
                });
            });
        },
        applyPromoCode() {
            this.promoApplied = true;
        },
        changePromocode() {
            this.$nextTick(() => {
                if (this.promoTimer) {
                    clearTimeout(this.promoTimer);
                }
                this.promoTimer = setTimeout(() => {
                    Vue.http.post(CHECK_PROMOCODE_URL, {
                        promocode: this.promocode
                    }).then((response) => {
                        this.promoData = response.body;
                        this.promoError = false;
                    }).catch(() => {
                        this.promoData = null;
                        this.promoError = true;
                    });
                }, 500);
            });
        },
        update() {
            this.complete = this.number && this.expiry && this.cvc && this.postalcode;

            // field completed, find field to focus next
            if (this.number) {
                if (!this.expiry) {
                    this.dynamicCardExpiry.focus();
                } else if (!this.cvc) {
                    this.dynamicCardCvc.focus();
                }
            } else if (this.expiry) {
                if (!this.cvc) {
                    this.dynamicCardCvc.focus();
                } else if (!this.number) {
                    this.dynamicCardNumber.focus();
                }
            }
            // no focus magic for the CVC field as it gets complete with three
            // numbers, but can also have four
        },
        changePlanToUpgrade() {
        },
        async upgradeToDualPlan() {
            if (!this.dualUpgradeInterval || this.dualUpgradeWorking) {
                return false;
            }
            this.dualUpgradeWorking = true;
            this.dualUpgradeError = '';
            try {
                let result = await this.$store.dispatch('upgradeNotesListToDual', {
                    interval: this.dualUpgradeInterval
                });
                if (result.mode === 'checkout' && result.url) {
                    stashPendingPlanUpdate(this.user);
                    window.location.href = result.url;
                    return true;
                }
                if (result.mode === 'requires_action') {
                    result = await this.handleRequiresAction(result.client_secret);
                    if (!result) {
                        this.dualUpgradeError = this.switchError;
                        return false;
                    }
                    result = { mode: 'swapped' };
                }
                if (result.mode === 'swapped' || result.mode === 'already_dual') {
                    let successMessage = 'Upgraded to Cuelist + Notes List. Sync will unlock shortly.';
                    if (result.mode === 'already_dual') {
                        successMessage = 'You already have that Cuelist + Notes List plan.';
                    } else if (this.isOnDual) {
                        successMessage = 'Switched Cuelist + Notes List billing. Updates apply shortly.';
                    }
                    this.$toast.success(successMessage);
                    this.closeDialog();
                    if (result.mode === 'swapped') {
                        await this.maybeResyncLinkedProjectAfterUpgrade();
                    }
                    return true;
                }
                this.dualUpgradeError = 'Unexpected response from billing. Please try again.';
                this.switchError = this.dualUpgradeError;
                return false;
            } catch (err) {
                this.dualUpgradeError = (err && err.message) || 'Upgrade failed. Please try again.';
                this.switchError = this.dualUpgradeError;
                return false;
            } finally {
                this.dualUpgradeWorking = false;
            }
        },
        upgradePlan() {
            this.doing = true;
            Vue.http.post(UPDATE_SUBSCRIPTION_URL, {
                customer_id: this.user.stripe_id,
                subscription_id: this.user.subscription_id
            }).then(() => {
                this.$store.dispatch('updateUser', {
                    uid: this.user.uid, 
                    update: {
                        subscription_level: 'yearly'
                    }
                });
                this.doing = false;
                this.closeDialog();
            }).catch(() => {
                this.doing = false;
            });
        },
        /**
         * After Dual / Sync-only upgrade unlocks access, push a full structure
         * sync for the open project when it is already linked to Notes List.
         * Stripe Checkout redirects leave the page — user re-syncs after return.
         */
        async maybeResyncLinkedProjectAfterUpgrade() {
            const project = this.project;
            if (
                !project ||
                !project.id ||
                project.noteslist_link_status !== 'linked' ||
                !project.noteslist_production_id
            ) {
                return;
            }
            try {
                await this.$store.dispatch('resyncNotesListProject', {
                    projectId: project.id
                });
                this.$toast.success('Full sync started with The Notes List');
            } catch (err) {
                // Access may lag Firestore briefly; leave Re-sync available.
                console.warn(
                    '[noteslist-integration] post-upgrade resync skipped:',
                    err && err.message
                );
                this.$toast.info(
                    'Upgrade complete. Click Re-sync in project settings if data is not updating yet.'
                );
            }
        }
    },
    components: {
        // Card,
        // CardNumber,
        // CardExpiry,
        // CardCvc,
        // PostalCode
    },
    mounted() {
    },
    beforeDestroy() {
    }
};
export default UpgradeAccount;
</script>

<style lang="scss">
.upgrade-account-modal {
    .md-dialog {
        min-width: 720px;
        max-width: 1180px;
        width: 96vw;
    }
    .upgrade-account-modal-content {
        position: relative;
        padding: 2rem;
        .close_modal {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 40px;
            height: 40px;
            line-height: 40px;
            font-size: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            overflow: hidden;
            cursor: pointer;
            color: #607d8b;
            &:hover {
                background: rgba(0, 0, 0, 0.06);
            }
        }
        .modal-title {
            font-size: 40px;
            line-height: 1.4;
        }
        .plan-current-line {
            margin: 8px 0 18px;
            font-size: 15px;
            color: #607d8b;
            .plan-current-name {
                color: #0d6e9c;
                font-weight: 600;
            }
        }
        .plan-cards {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
            margin: 1rem 0 0.5rem;
            align-items: stretch;
            &.plan-cards--edu-inst {
                grid-template-columns: repeat(4, minmax(0, 1fr));
            }
            @media (max-width: 1100px) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                &.plan-cards--edu-inst {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
            }
            @media (max-width: 640px) {
                grid-template-columns: 1fr;
                &.plan-cards--edu-inst {
                    grid-template-columns: 1fr;
                }
            }
        }
        .plan-card {
            display: flex;
            flex-direction: column;
            background: #fff;
            border: 1px solid #d7dee3;
            border-radius: 10px;
            padding: 18px 16px 16px;
            box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
            height: 100%;
            min-height: 100%;
            box-sizing: border-box;
            &.is-selected,
            &.is-current {
                border-color: #0d6e9c;
                box-shadow: 0 0 0 1px #0d6e9c;
            }
            &.is-pending-confirm {
                border-color: #0d6e9c;
                background: #eaf6fb;
                box-shadow: 0 0 0 2px #0d6e9c;
            }
            &.is-unavailable {
                cursor: no-drop;
                .plan-card-title,
                .plan-card-amount,
                .plan-card-features,
                .plan-card-eyebrow {
                    color: #90a4ae;
                }
                .plan-card-btn,
                .plan-card-btn:disabled {
                    cursor: no-drop;
                }
            }
            &.show-md-dark-mode {
                background: #2f2f2f;
                border-color: rgba(255, 255, 255, 0.18);
                box-shadow: none;
                &.is-selected,
                &.is-current {
                    border-color: #73d5e3;
                    box-shadow: 0 0 0 1px #73d5e3;
                }
                &.is-pending-confirm {
                    background: rgba(13, 110, 156, 0.28);
                    border-color: #73d5e3;
                    box-shadow: 0 0 0 2px #73d5e3;
                }
                &.is-unavailable {
                    .plan-card-title,
                    .plan-card-amount,
                    .plan-card-features,
                    .plan-card-interval,
                    .plan-card-eyebrow {
                        color: rgba(255, 255, 255, 0.38);
                    }
                }
                .plan-card-eyebrow {
                    color: rgba(255, 255, 255, 0.55);
                    &--dual {
                        color: #73d5e3;
                    }
                }
                .plan-card-badge {
                    background: rgba(115, 213, 227, 0.22);
                    color: #73d5e3;
                }
                .plan-card-title {
                    color: #e8eef5;
                }
                .plan-card-amount {
                    color: #fff;
                }
                .plan-card-interval {
                    color: rgba(255, 255, 255, 0.55);
                }
                .plan-card-features li {
                    color: rgba(255, 255, 255, 0.7);
                    &:before {
                        background: rgba(255, 255, 255, 0.45);
                    }
                }
                .plan-card-btn--outline {
                    background: transparent;
                    color: #e8eef5;
                    border-color: rgba(255, 255, 255, 0.35);
                    &:hover {
                        border-color: #73d5e3;
                        color: #fff;
                    }
                }
                .plan-card-btn--disabled {
                    background: rgba(255, 255, 255, 0.12);
                    color: rgba(255, 255, 255, 0.45);
                    border-color: transparent;
                }
                .plan-card-btn--primary {
                    background: #73d5e3;
                    color: #000;
                    border-color: #73d5e3;
                    &:hover {
                        background: #5fc4d4;
                    }
                }
            }
            .plan-card-top {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                min-height: 22px;
            }
            .plan-card-eyebrow {
                font-size: 11px;
                font-weight: 700;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: #90a4ae;
                &--dual {
                    color: #0d6e9c;
                }
            }
            .plan-card-badge {
                display: inline-flex;
                align-items: center;
                padding: 2px 8px;
                border-radius: 999px;
                background: #d6ebf5;
                color: #0d6e9c;
                font-size: 10px;
                font-weight: 700;
                letter-spacing: 0.04em;
                text-transform: uppercase;
            }
            .plan-card-title {
                margin-top: 10px;
                font-size: 17px;
                font-weight: 700;
                line-height: 1.25;
                color: #263238;
                min-height: calc(1.25em * 2);
            }
            .plan-card-price {
                margin-top: 10px;
                margin-bottom: 8px;
                display: flex;
                align-items: baseline;
                gap: 4px;
            }
            .plan-card-amount {
                font-size: 28px;
                font-weight: 700;
                color: #1a2329;
                line-height: 1;
            }
            .plan-card-interval {
                font-size: 14px;
                color: #78909c;
            }
            .plan-card-features {
                list-style: none;
                margin: 14px 0 18px;
                padding: 0;
                flex: 1 1 auto;
                li {
                    position: relative;
                    padding-left: 14px;
                    margin: 0 0 8px;
                    font-size: 13px;
                    line-height: 1.35;
                    color: #607d8b;
                    &:before {
                        content: '';
                        position: absolute;
                        left: 0;
                        top: 7px;
                        width: 5px;
                        height: 5px;
                        border-radius: 50%;
                        background: #90a4ae;
                    }
                }
            }
            .plan-card-btn {
                width: 100%;
                margin-top: auto;
                appearance: none;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                border-radius: 8px;
                padding: 10px 12px;
                font-size: 14px;
                font-weight: 600;
                line-height: 1.2;
                cursor: pointer;
                border: 1px solid transparent;
                transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
                .plan-card-btn-spinner {
                    width: 14px;
                    height: 14px;
                    border: 2px solid currentColor;
                    border-right-color: transparent;
                    border-radius: 50%;
                    animation: plan-option-spin 0.7s linear infinite;
                    flex-shrink: 0;
                }
                &--primary {
                    background: #0d6e9c;
                    color: #fff;
                    border-color: #0d6e9c;
                    &:hover {
                        background: #0a5a80;
                    }
                }
                &--outline {
                    background: #fff;
                    color: #263238;
                    border-color: #cfd8dc;
                    &:hover {
                        border-color: #90a4ae;
                    }
                }
                &--disabled {
                    background: #eceff1;
                    color: #90a4ae;
                    border-color: #eceff1;
                    cursor: no-drop;
                }
                &--danger {
                    background: #c62828;
                    color: #fff;
                    border-color: #c62828;
                    &:hover {
                        background: #b71c1c;
                    }
                }
                &:disabled {
                    cursor: no-drop;
                    opacity: 1;
                }
            }
        }
        .plan-switch-note {
            margin: 16px 0 0;
            font-size: 13px;
            line-height: 1.4;
            color: #78909c;
        }
        .plan-cancel-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            flex-wrap: wrap;
            margin-top: 22px;
            padding-top: 18px;
            border-top: 1px solid #e0e6eb;
            .plan-cancel-footer-label {
                font-size: 14px;
                color: #78909c;
            }
            .plan-cancel-link {
                appearance: none;
                background: none;
                border: none;
                padding: 0;
                color: #c62828;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                text-decoration: underline;
                margin-left: auto;
                &:disabled {
                    color: #b0bec5;
                    cursor: default;
                    text-decoration: none;
                }
            }
        }
        .plan-cancel-panel {
            margin-top: 28px;
            .plan-cancel-link {
                appearance: none;
                background: none;
                border: none;
                padding: 0;
                color: #c62828;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                text-decoration: underline;
                &:disabled {
                    color: #b0bec5;
                    cursor: default;
                    text-decoration: none;
                }
            }
        }
        .plan-cancel-box {
            margin-top: 8px;
            padding: 20px 20px 18px;
            border-radius: 10px;
            background: #fdecea;
            border: 1px solid #ef9a9a;
            .plan-cancel-box-title {
                font-size: 18px;
                font-weight: 700;
                color: #263238;
                margin-bottom: 10px;
            }
            .plan-cancel-box-body {
                margin: 0 0 12px;
                font-size: 14px;
                line-height: 1.45;
                color: #455a64;
            }
            .plan-cancel-note {
                margin: 0 0 16px;
                padding: 12px 14px;
                border-radius: 8px;
                background: #fff;
                border: 1px solid #e0e0e0;
                font-size: 13px;
                line-height: 1.45;
                color: #546e7a;
            }
            .plan-cancel-actions {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
            }
            .plan-cancel-btn {
                appearance: none;
                -webkit-appearance: none;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                min-width: 148px;
                height: 40px;
                padding: 0 16px;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 600;
                line-height: 1;
                cursor: pointer;
                box-sizing: border-box;
                .plan-cancel-btn-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.35);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: plan-option-spin 0.7s linear infinite;
                    flex-shrink: 0;
                }
                &--keep {
                    background: #fff;
                    color: #263238;
                    border: 1px solid #cfd8dc;
                    &:hover {
                        border-color: #90a4ae;
                    }
                }
                &--confirm {
                    background: #c62828;
                    color: #fff;
                    border: 1px solid #c62828;
                    &:hover {
                        background: #b71c1c;
                        border-color: #b71c1c;
                    }
                    &:disabled {
                        opacity: 0.65;
                        cursor: default;
                    }
                }
            }
        }
        // Dark Mode — cancel confirmation panel
        &.show-md-dark-mode,
        .show-md-dark-mode & {
            .plan-cancel-box {
                background: rgba(198, 40, 40, 0.18);
                border-color: rgba(239, 154, 154, 0.45);
                .plan-cancel-box-title {
                    color: #ffcdd2;
                }
                .plan-cancel-box-body {
                    color: rgba(232, 238, 245, 0.85);
                    strong {
                        color: #fff;
                    }
                }
                .plan-cancel-note {
                    background: rgba(0, 0, 0, 0.28);
                    border-color: rgba(255, 255, 255, 0.16);
                    color: rgba(232, 238, 245, 0.75);
                }
                .plan-cancel-btn--keep {
                    background: rgba(255, 255, 255, 0.1);
                    color: #e8eef5;
                    border-color: rgba(255, 255, 255, 0.3);
                    &:hover {
                        border-color: #73d5e3;
                        color: #fff;
                    }
                }
                .plan-cancel-btn--confirm {
                    background: #c62828;
                    color: #fff;
                    border-color: #c62828;
                }
            }
        }
        .plan-type {
            margin-top: 0.35rem;
            width: fit-content;
            max-width: 100%;
            span {
                display: block;
                width: fit-content;
                margin-bottom: 4px;
                cursor: pointer;
                color: #106d9a;
                text-decoration: underline;
                font-size: 16px;
                line-height: 1.4;
            }
        }
        .plan-switch-panel .plan-type {
            margin-bottom: 0.25rem;
        }
        .plan-type span.plan-type-apart {
            margin-top: 14px;
        }
        .plan-current-student-end {
            display: block;
            margin-top: 4px;
            font-size: 14px;
        }
        .plan-student-end {
            margin-top: 8px;
        }
        .plan-state-banner {
            margin: 14px 0 8px;
            padding: 12px 14px;
            border-radius: 6px;
            font-size: 14px;
            line-height: 1.4;
            &--info {
                background: #e3f2fd;
                color: #0d47a1;
                a {
                    color: #0d47a1;
                    font-weight: 600;
                }
            }
            &--success {
                background: #e8f5e9;
                color: #1b5e20;
            }
            &--warning {
                background: #fff3e0;
                color: #e65100;
            }
            &--with-action {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                gap: 10px 16px;
            }
        }
        // Dark Mode banners (parent has show-md-dark-mode when Dark Mode is on)
        &.show-md-dark-mode,
        .show-md-dark-mode & {
            .plan-state-banner--info {
                background: rgba(33, 150, 243, 0.18);
                color: #90caf9;
                a {
                    color: #73d5e3;
                }
            }
            .plan-state-banner--success {
                background: rgba(76, 175, 80, 0.18);
                color: #a5d6a7;
                a, strong {
                    color: #c8e6c9;
                }
            }
            .plan-state-banner--warning {
                background: rgba(255, 152, 0, 0.18);
                color: #ffcc80;
            }
            .plan-banner-action-btn {
                background: rgba(255, 255, 255, 0.1);
                border-color: rgba(255, 255, 255, 0.3);
                color: #e8eef5;
                &:hover {
                    border-color: #73d5e3;
                }
                .plan-banner-action-spinner {
                    border-color: rgba(255, 255, 255, 0.3);
                    border-top-color: #fff;
                }
            }
            .plan-confirm-panel {
                background: rgba(13, 110, 156, 0.22);
                border-color: rgba(115, 213, 227, 0.45);
                color: #e8eef5;
            }
        }
        .plan-banner-action-btn {
            flex-shrink: 0;
            appearance: none;
            -webkit-appearance: none;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            height: 32px;
            padding: 0 14px;
            border-radius: 6px;
            background: #fff;
            border: 1px solid rgba(0, 0, 0, 0.2);
            color: inherit;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            .plan-banner-action-spinner {
                width: 14px;
                height: 14px;
                border: 2px solid rgba(0, 0, 0, 0.18);
                border-top-color: #263238;
                border-radius: 50%;
                animation: plan-option-spin 0.7s linear infinite;
                flex-shrink: 0;
            }
            &:hover {
                border-color: rgba(0, 0, 0, 0.4);
            }
            &:disabled {
                opacity: 0.6;
                cursor: default;
            }
        }
        .plan-confirm-panel {
            margin-top: 16px;
            padding: 14px 16px;
            border: 1px solid #b6e0ef;
            border-left: 4px solid #0d6e9c;
            border-radius: 8px;
            background: #eaf6fb;
        }
        .plan-confirm-panel-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
        }
        .plan-confirm-panel-icon {
            color: #0d6e9c;
            flex-shrink: 0;
        }
        .plan-confirm-panel-title {
            font-size: 15px;
            font-weight: 700;
            color: #0d6e9c;
        }
        .plan-confirm-panel-plan {
            margin: 0 0 6px;
            font-size: 13px;
            font-weight: 600;
            color: #0d2744;
        }
        .plan-confirm-panel-amount {
            margin: 0 0 10px;
            font-size: 14px;
            color: #0d2744;
            strong {
                font-size: 16px;
            }
        }
        .plan-confirm-panel-error {
            margin: 0 0 10px;
            font-size: 13px;
            color: #c62828;
        }
        .plan-confirm-panel-actions {
            display: flex;
            align-items: center;
            gap: 14px;
            .btn-subscribe-plan {
                width: auto;
                min-width: 220px;
                background: rgb(21, 144, 181) !important;
                color: #fff !important;
                margin: 0;
                .md-button-content {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .plan-card-btn-spinner {
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(255, 255, 255, 0.4);
                    border-right-color: transparent;
                    border-radius: 50%;
                    animation: plan-option-spin 0.7s linear infinite;
                    flex-shrink: 0;
                }
                font-size: 15px;
                line-height: 1.4;
                padding: 0 20px;
            }
            .btn-subscribe-plan:disabled {
                background: #909090 !important;
                pointer-events: unset !important;
                cursor: not-allowed !important;
            }
        }
        .plan-confirm-panel-cancel {
            font-size: 13px;
            color: rgba(13, 39, 68, 0.72);
            &:hover {
                color: #0d2744;
            }
        }
        .plan-options-wrapper {
            margin: 1rem 0;
            .plan-state-banner {
                margin: 14px 0 8px;
                padding: 12px 14px;
                border-radius: 6px;
                font-size: 14px;
                line-height: 1.4;
                &--info {
                    background: #e3f2fd;
                    color: #0d47a1;
                    a {
                        color: #0d47a1;
                        font-weight: 600;
                    }
                }
                &--success {
                    background: #e8f5e9;
                    color: #1b5e20;
                }
            }
            .plan-option--disabled {
                opacity: 0.55;
                cursor: no-drop !important;
                border-color: #cfd8dc !important;
                * {
                    cursor: no-drop !important;
                }
            }
            .plan-option--loading {
                opacity: 0.85;
                cursor: wait !important;
                * {
                    cursor: wait !important;
                }
            }
            .plan-option-status {
                display: inline-flex !important;
                flex-direction: row;
                align-items: center;
                flex-wrap: nowrap;
                gap: 8px;
                margin-top: 6px;
                font-size: 12px;
                font-weight: 600;
                line-height: 1;
                color: #607d8b;
                white-space: nowrap;
            }
            .plan-option-spinner {
                display: inline-block !important;
                width: 14px;
                height: 14px;
                border: 2px solid #cfd8dc;
                border-top-color: #084b73;
                border-radius: 50%;
                animation: plan-option-spin 0.7s linear infinite;
                flex-shrink: 0;
                vertical-align: middle;
            }
            @keyframes plan-option-spin {
                to { transform: rotate(360deg); }
            }
            .plan-options {
                margin: 1rem 0;
                display: flex;
                align-items: stretch;
                flex-wrap: wrap;
                gap: 1rem;
                &.plan-options--individual,
                &.plan-options--edu-inst {
                    .plan-option {
                        flex: 1 1 0;
                        min-width: 0;
                        margin-right: 0;
                    }
                }
                // Four Edu/Inst cards: 2×2 so Dual Institutional is never clipped
                &.plan-options--edu-inst {
                    max-width: 100%;
                    .plan-option {
                        flex: 1 1 calc(50% - 0.5rem);
                        min-width: 200px;
                    }
                }
                .plan-option.show-md-dark-mode {
                    &.active {
                        background: #8f8f8f;
                        &:before {
                            background: #084b73;
                        }
                    }
                }
                    .plan-option {
                        display: flex;
                        align-items: center;
                        padding: 10px 12px;
                        min-height: 72px;
                        border: solid 2px #084b73;
                        cursor: pointer;
                        font-size: 16px;
                        line-height: 1.25;
                        margin-right: 0;
                        box-sizing: border-box;
                    .plan-option-title {
                        margin-left: 10px;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        min-width: 0;
                        .plan-option-name,
                        .plan-option-price {
                            display: block;
                            text-align: left;
                        }
                        .plan-option-name {
                            font-weight: 600;
                            font-size: 14px;
                            line-height: 1.2;
                        }
                        .plan-option-price {
                            font-size: 15px;
                            line-height: 1.3;
                            margin-top: 2px;
                        }
                        span {
                            display: block;
                            text-align: left;
                        }
                        .plan-option-status {
                            display: inline-flex !important;
                            flex-direction: row;
                            align-items: center;
                        }
                        .plan-option-spinner {
                            display: inline-block !important;
                        }
                    }
                    &:before {
                        content: '';
                        width: 20px;
                        height: 20px;
                        min-width: 20px;
                        border-radius: 50%;
                        border: solid 1px #35d3de;
                        display: block;
                        flex-shrink: 0;
                    }
                    &.active {
                        background: #a9e0e6;
                        &:before {
                            background: #084b73;
                        }
                    }
                }
            }
            .plan-options-note {
                font-size: 16px;
            }
        }
        .promo-code-wrapper {
            max-width: 400px;
            .btn-apply-promo-code {
                cursor: pointer;
                color: #106d9a;
                text-decoration: underline;
                font-size: 15px;
                line-height: 1.4;
                margin: 0;
            }
        }
        .plan-subscribe-content-wrapper {
            margin-top: 1rem;
            .plan-subscribe-content-container {
                min-width: 900px;
                .plan-subscribe-content {
                    width: 100%;
                    padding-right: 25px;
                    .card-area {
                        width: 100%;
                        margin-bottom: 1rem;
                    }

                    .credit-card-inputs {
                        .StripeElement {
                            border: solid 1px #ccc;
                            padding: 8px 12px;
                            margin-bottom: 15px;
                            &.StripeElement--invalid {
                                border: solid 1px #e5424d;
                            }
                        }
                        .stripe-element {
                            flex: 1;
                            &.card-expiry {
                                margin-right: 15px;
                            }
                            &.card-cvc {
                                margin-right: 15px;
                            }
                            &.country {
                                min-width: 60%;
                                margin-left: 15px;
                                select {
                                    display: block;
                                    cursor: pointer;
                                    background: #eee;
                                    width: 100%;
                                    height: 42px;
                                    font-size: 16px;
                                    border-radius: 0;
                                    padding: 0 10px;
                                    -webkit-appearance: none;
                                    appearance: none;
                                    outline: 0 none;
                                }
                            }
                        }
                        .stripe-element-row {
                            display: flex;
                            justify-content: space-between;
                        }
                    }

                    .institutional-additions {
                        input {
                            width: 100%;
                            color: #32325d;
                            line-height: 24px;
                            font-family: Helvetica Neue, sans-serif;
                            font-size: 16px;
                            border: solid 1px #ccc;
                            padding: 8px 12px;
                        }
                    }
                    .plan-payment-notification.show-md-dark-mode {
                        background: unset;
                    }
                    .plan-payment-notification {
                        background: #e5e5e5;
                        padding: 15px;
                        margin: 1rem 0;
                        .notification-title {
                            font-size: 20px;
                            line-height: 1.7;
                            margin: 10px 0;
                        }
                        .notification-message {
                            margin-bottom: 10px;
                            span {
                                font-weight: 600;
                            }
                        }
                    }
                    .plan-subscribe-button-wrapper {
                        margin-bottom: 1rem;
                        .btn-subscribe-plan {
                            width: 100%;
                            background: rgb(21, 144, 181) !important;
                            color: #fff !important;
                            margin: 0;
                            font-size: 24px;
                            line-height: 1.7;
                            &.complete {
                                background: rgb(21, 144, 181) !important;
                            }
                            &.doing {
                                opacity: 0.7;
                            }
                        }
                        .btn-subscribe-plan:disabled {
                            background: #909090 !important;
                            pointer-events: unset !important;
                            cursor: not-allowed !important;
                        }
                    }
                    .subscription-terms-privacy {
                        .md-checkbox-label {
                            height: auto;
                        }
                        a {
                            color: rgb(22, 143, 177);
                        }
                    }
                }
                .billing-details.show-md-dark-mode {
                    background: unset;
                }
                .billing-details {
                    background: #e5e5e5;
                    padding: 25px;
                    place-self: flex-start;
                    .billing-details-title {
                        font-size: 18px;
                        font-weight: 600;
                    }
                    .billing-details-checklist {
                        font-size: 11px;
                        padding: 0 0 15px;
                        border-bottom: solid 1px #aaa;
                        li {
                            margin: 0;
                            list-style: none;
                            display: flex;
                            align-items: baseline;
                            &:before {
                                content: '';
                                display: block;
                                width: 6px;
                                height: 6px;
                                border-radius: 50%;
                                background: rgb(22, 144, 181);
                                margin-right: 8px;
                            }
                        }
                    }
                    .billing-details-list {
                        font-size: 13px;
                    }
                    .billing-details-list--showpass {
                        .showpass-row {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 4px;
                        }
                        .showpass-row--total {
                            font-weight: 600;
                        }
                    }
                }
                .billing-total-info {
                    margin-top: 1rem;
                    background: rgb(216, 250, 254);
                    color: rgb(11, 98, 129);
                    padding: 25px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    .billing-total {
                        font-size: 18px;
                        margin-bottom: 1rem;
                        font-weight: 600;
                        display: flex;
                        justify-content: space-between;
                    }
                }
            }
        }
    }
    .md-error {
        color: #e5424d;
        font-size: 12px;
    }
    &.has-plan .plan-subscribe-content-container {
        min-width: 600px !important;
    }
    .md-input-success .md-input {
        border-bottom: solid 2px #009a67;
    }
    .stripe-element-column-wrapper {
        display: flex;
        flex-direction: column;
    }
    .stripe-element-change-plan {
        padding: 1rem;
        background: #dff9fd;
        width: 650px;
        .subscription-upgrade-options-wrapper {
            display: flex;
            align-items: center;
            .subscription-upgrade-options {
                margin-top: 6px;
                .md-radio {
                    margin: 0 0 6px;
                }
            }
            .md-button {
                min-width: 150px;
            }
        }
    }
}
</style>


// WEBPACK FOOTER //
// upgrade-account.vue?545c0ffb